/**
 * Java 8 parser.
 *
 * This code is performance sensitive! When making big changes please consider benchmarking.
 *
 * GENERAL OVERVIEW
 *
 * We use a straightforward recursive-descent approach, with an operator-precedence parser for the binary operator
 * expressions. In particular, all unary operators in Java (++, --, +, -, ~, !) bind tighter than binary operators, so
 * we may parse a general expression containing binary operators using an operator-precedence parser, with the leafs
 * being unary expressions around "primary expressions", which are the lowest level of expression, not being an
 * application of an operator.
 *
 * For example, in the expression a.cow() + b++, the whole expression is a binary operator (plus) as parsed by
 * consumeExpressionOperatorPrecedence; the "a.cow()" and "b" are indivisible primary expressions, and "b++" is a
 * unary expression.
 *
 * LOOKAHEAD
 *
 * There are a few places where ambiguities are difficult to resolve without a large lookahead. Technically it is
 * possible to write an LL(k) Java parser, but it is a bit painful and can also make error messages a bit trickier to
 * write. An example of an ambiguity is determining whether a statement starting with a type, such as
 * ArrayList<Integer>, is actually a local variable declaration (e.g. ArrayList<Integer> b = null;) or a usage
 * of that type within a standalone expression (e.g. ArrayList<Integer>::new.getName()). (The former is much more
 * common, but we still need to handle both.) Of course, if something speculatively parsed has a syntax error, we don't
 * want to actually emit those errors. This functionality is somewhat hacked in with the `captureErrors` function,
 * which temporarily reassigns where errors are reported in case you want to suppress them.
 *
 * ERRONEOUS EXPRESSIONS
 *
 * ErrorTrees are used in certain expression contexts when something fails to parse, so as to still allow the
 * emission of an expression tree, with one part being not-understood. For example, in a * int, the int becomes
 * an ErrorTree (as it's obviously illegal to use a primitive type in that context), but the whole thing still parses
 * as a binary operation between a PlainIdentifier and an int.
 *
 * AMBIGUOUS NAMES
 *
 * The Java grammar has the concept of an ambiguous name: a tree whose interpretation is unknown until type and scope
 * information is present. For example, F.cow() is always a method invocation, but it could be on the static function
 * of an F class or a member function of a variable named F. Generally, names become either PlainIdentifiers (if they
 * lack dot namespaces, e.g. "ArrayList") or QualifiedIdentifiers (e.g. java.util.ArrayList).
 *
 * Ambiguity resolution is done in analysis.ts, not in the parser.
 */

import {
	AssignmentOrCompoundAssignment,
	Comment,
	isAssignmentOrCompoundAssignment,
	isKeyword,
	isPrimitive,
	Keyword,
	Lexer,
	Misc,
	Operator,
	PrimitiveKw,
	Separator,
	Token,
	TokenIdentifier,
	TokenOfType,
	TokenType,
	tokenTypeToLength,
	tokenTypeToString,
} from "./lexer";
import {
	AnnotationTree,
	AnonymousClassCreationTree,
	ArrayAccessTree,
	ArrayCreationTree,
	ArrayInitializerTree,
	ArrayTypeTree,
	AssertTree,
	AssignmentTree,
	BaseExpressionTree,
	BaseTree,
	BinaryOperator,
	BinaryOperatorTree,
	BlockTree,
	BooleanLiteralTree,
	BoundsTree,
	BracketsTree,
	BreakStatementTree,
	CaseStatementTree,
	CatchStatementTree,
	CharacterLiteralTree,
	ClassCreationTree,
	ClassDeclarationTree,
	ClassLiteralTree,
	CompilationUnitTree,
	ContinueStatementTree,
	DoWhileLoopTree,
	EmptyStatementTree,
	EnhancedForLoopTree,
	EnumConstantTree,
	EnumDeclarationTree,
	ErrorTree,
	ExpressionStatementListTree,
	ExpressionStatementTree,
	FieldAccessTree,
	FinallyStatementTree,
	FloatLiteralTree,
	ForLoopTree,
	FunctionArgumentsTree,
	FunctionArgumentTree,
	FunctionDeclarationTree,
	IdentTree,
	IfStatementTree,
	ImportDeclarationTree,
	IntegerLiteralTree,
	InterfaceDeclarationTree,
	LabelledStatementTree,
	LambdaExpressionTree,
	LocalVariableDeclarationListTree,
	LocalVariableDeclarationTree,
	MethodInvocationTree,
	MethodReferenceTree,
	ModifierTree,
	NameTree,
	NullLiteralTree,
	PackageDeclarationTree,
	ParenthesizedExpressionTree,
	PrimitiveTypeTree,
	ReferenceTypeTree,
	ReturnStatementTree,
	StaticInitializationBlockTree,
	StringLiteralTree,
	SuperTree,
	SwitchStatementTree,
	SynchronizedStatementTree,
	TernaryOperatorTree,
	ThisTree,
	ThrowStatementTree,
	ThrowsTree,
	TryStatementTree,
	TypeArgumentsTree,
	TypeCastTree,
	TypeParametersTree,
	TypeParameterTree,
	UnaryOperatorTree,
	WhileLoopTree,
	WildcardTree,
} from "./model";
import {cafebabeAssert, CompileError, JavaVersion, Span} from "./error";

export type ParserOptions = {
	// Force the final token in the stream, if it is not succeeded by whitespace, to be interpreted as an identifier
	// if it is a keyword. Useful for completions, so that e.g. integer.long| will be recognized as an identifier, so
	// that we can add completions like "longValue".
	forceFinalIdentifier?: boolean;

	// Java version to parse
	javaVersion?: JavaVersion;
};

// Used mostly for error recovery -- finding suitable tokens to skip to
const SyntaxFacts = {
	StartImportOrTypeDeclOrPackage: new Set<Token["type"]>([
		Keyword.import,
		Keyword.class,
		Keyword.interface,
		Keyword.enum,
		Keyword.public,
		Keyword.protected,
		Keyword.private,
		Keyword.static,
		Keyword.abstract,
		Keyword.final,
		Separator.At,
		Separator.Semicolon,
		Keyword.package,
	]),
	TypeDeclarationModifierOrStart: new Set<Token["type"]>([
		Separator.At,
		Keyword.public,
		Keyword.protected,
		Keyword.private,
		Keyword.abstract,
		Keyword.static,
		Keyword.final,
		Keyword.strictfp,
		Keyword.interface,
		Keyword.enum,
		Keyword.class,
	]),
	StatementStartOrTerminal: new Set<Token["type"]>([
		// Start
		Keyword.while,
		Keyword.for,
		Keyword.if,
		Keyword.else,
		Keyword.switch,
		Keyword.do,
		Keyword.try,
		Keyword.catch,
		Keyword.finally,
		Keyword.return,
		Keyword.break,
		Keyword.continue,
		Keyword.throw,
		Keyword.assert,
		Keyword.synchronized,

		// terminal
		Separator.Semicolon,
		Separator.RBrace,
	]),
	PrimaryExpressionStart: new Set<Token["type"]>([
		Misc.identifier,
		Keyword.new,
		Keyword.this,
		Keyword.super,
		Keyword.int,
		Keyword.char,
		Keyword.byte,
		Keyword.short,
		Keyword.long,
		Keyword.float,
		Keyword.double,
		Keyword.boolean,
		Keyword.void,
		Separator.LParen,
		Separator.LBracket,
		Misc.null,
		Misc.string,
		Misc.characterLiteral,
		Misc.floatLiteral,
		Misc.integer,
		Misc.booleanLiteral,
	]),
	UnaryExpressionNotPlusMinusStart: new Set<Token["type"]>([
		Keyword.this,
		Keyword.super,
		Keyword.new,
		Keyword.boolean /* boolean.class */,
		Keyword.void /* void.class */,
		Keyword.int,
		Keyword.long,
		Keyword.short,
		Keyword.byte,
		Keyword.char,
		Keyword.float,
		Keyword.double,
		Misc.identifier,
		Misc.booleanLiteral,
		Misc.null,
		Misc.characterLiteral,
		Misc.integer,
		Misc.string,
		Operator.Not,
		Operator.Tilde,
		Misc.floatLiteral,

		Separator.LParen,
	]),
	DefinitelyBinaryOperator: new Set<Token["type"]>([
		Keyword.instanceof,
		Operator.Star,
		Operator.StarEq,
		Operator.PlusEq, // TODO
	]),
	// Example: ArrayList.class shouldn't be parsed as a type ArrayList.class, but rather as a ClassLiteralTree
	NotTrueFieldAccessRhs: new Set<Token["type"]>([
		Keyword.class,
		Keyword.super,
		Operator.Lt,
		Keyword.new,
	]),
};

enum QualIdentMode {
	Normal = 0,
	AllowTrailingStar = 1,
	// useful for expressions like List.<Integer>of(). Allows Lt, super, class, and this to follow
	AllowTrailingForPrimary = 2,
}

/**
 * Given a partially completed primary expression, what continuations are allowed to come after.
 */
enum ExprRest {
	None = 0,
	ArrayAccess = 1,
	FieldAccess = 2,
	ThisAccess = 4,
	SuperAccess = 8,
	// LHS may be considered itself a function to be immediately invoked with arguments, e.g. Math.random [()]
	ImmediateInvocation = 16,
	// LHS may be considered the start of a function
	QualifiedInvocation = 32,
	MethodReference = 64,
	// e.g. int [.class]
	ClassLiteral = 128,
	NewExpression = 256,
	All = -1,
}

const BinaryPrecedenceTable: {[key in Operator | Keyword.instanceof]?: number} =
	{
		// e.g. a || b && c always parses as a || (b && c)
		[Operator.OrOr]: 1,
		[Operator.AndAnd]: 2,
		[Operator.Or]: 3,
		[Operator.Caret]: 4,
		[Operator.And]: 5,
		[Operator.EqEq]: 6,
		[Operator.NotEq]: 6,
		[Operator.Lt]: 7,
		[Operator.Gt]: 7,
		[Operator.LtEq]: 7,
		[Operator.GtEq]: 7,
		[Keyword.instanceof]: 7,
		[Operator.LtLt]: 8,
		[Operator.GtGt]: 8,
		[Operator.GtGtGt]: 8,
		[Operator.Plus]: 9,
		[Operator.Minus]: 9,
		[Operator.Star]: 10,
		[Operator.Slash]: 10,
		[Operator.Percent]: 10,
	};

enum GuessedNameClass {
	TypeName,
	VariableName,
}

// Used for some errors, but shouldn't be used for parsing proper obviously
function vaguelyClassifyName(name: string): GuessedNameClass {
	return name[0] === name[0]?.toLowerCase()
		? GuessedNameClass.VariableName
		: GuessedNameClass.TypeName;
}

type BraceToken = TokenOfType<
	| Separator.LParen
	| Separator.LBracket
	| Separator.LBrace
	| Separator.RParen
	| Separator.RBracket
	| Separator.RBrace
>;

/**
 * Java 8 parser.
 */
export class Parser {
	readonly lexer: Lexer;

	// List of tokens
	public readonly tokens: Token[];
	// current token index
	public currentIndex: number;
	// current index within a split >=, >>, >>=, >>>, or >>>= token
	splitIndex = 0;
	// # of times we entered type mode (increment to enter, decrement to exit)
	typeMode = 0;
	// List of errors generated during the parser
	public errors: CompileError[] = [];

	// Fuzzy bracket matching -- may need refinement
	public readonly matchingBrackets: Map<
		Token,
		{match: Token | null; tokenIndex: number; erroneous: boolean}
	> = new Map();

	/**
	 * Create a new Java parser of some text.
	 * @param text Text to parse.
	 * @param options Parser options (e.g. disallow some expressions)
	 */
	constructor(
		text: string,
		readonly options: ParserOptions = {}
	) {
		const lexer = (this.lexer = new Lexer(text, this.options));
		const tokens: Token[] = [];
		let t: Token;

		// Collect all tokens including EOF
		do {
			t = lexer.next();
			tokens.push(t);
		} while (t.type !== Misc.eof);
		lexer.finish(); // fixup lexer spans due to unicode escapes and add lexer errors
		this.errors.push(...lexer.errors);
		this.tokens = tokens;
		this.currentIndex = 0;

		if (options.forceFinalIdentifier) this._forceFinalIdentifier();
		this._fuzzyMatchBrackets();
	}

	/**
	 * Get the Java version to parse.
	 */
	javaVersion(): JavaVersion {
		return this.options.javaVersion ?? 8;
	}

	/**
	 * Please see ParserOptions.forceFinalIdentifier
	 */
	private _forceFinalIdentifier() {
		const penultimateToken = this.tokens.at(-2); // token before EOF
		if (!penultimateToken) return; // empty parse?
		const tokenType = penultimateToken.type;
		if (isKeyword(tokenType)) {
			penultimateToken.type = Misc.identifier;
			(penultimateToken as TokenIdentifier).value =
				tokenTypeToString(tokenType);
		}
	}

	/**
	 * Attempt to match brackets.
	 *
	 * The algorithm is imprecise and subject to improvements, in particular factoring in match information from
	 * previous parses. We just pop the most recent bracket which matches, and if this isn't the bracket at the top
	 * of the stack, we mark the match as erroneous.
	 */
	private _fuzzyMatchBrackets() {
		const bracketStack: (Token & {
			type: Separator.LBracket | Separator.LBrace | Separator.LParen;
		})[] = [];
		const indexStack: number[] = [];
		const {tokens, matchingBrackets} = this;

		// Pop an opening bracket off the stack and pair it with right.
		function pop(
			right: Separator.LParen | Separator.LBracket | Separator.LBrace,
			tok: Token,
			tokenIndex: number
		) {
			const startIndex = bracketStack.length - 1;
			for (let j = startIndex; j >= 0; --j) {
				const match = bracketStack[j]!;
				if (match!.type === right) {
					const erroneous = j !== startIndex;
					matchingBrackets.set(match, {match: tok, erroneous, tokenIndex});
					matchingBrackets.set(tok, {
						match,
						erroneous,
						tokenIndex: indexStack[j]!,
					});
					bracketStack.splice(j, 1);
					return;
				}
			}
			matchingBrackets.set(tok, {match: null, erroneous: true, tokenIndex: -1}); // failed to find match
		}

		for (let i = 0; i < tokens.length; ++i) {
			const tok = tokens[i]!;
			switch (tok.type) {
				case Separator.RParen:
					pop(Separator.LParen, tok, i);
					break;
				case Separator.RBracket:
					pop(Separator.LBracket, tok, i);
					break;
				case Separator.RBrace:
					pop(Separator.LBrace, tok, i);
					break;
				case Separator.LBrace:
				case Separator.LParen:
				case Separator.LBracket:
					bracketStack.push(tok as any);
					indexStack.push(i);
					break;
			}
		}

		// Mark all unmatched brackets as erroneous
		bracketStack.forEach((tok) => {
			matchingBrackets.set(tok, {match: null, erroneous: true, tokenIndex: -1});
		});
	}

	/**
	 * Get the bracket matching the opening or closing bracket given.
	 * @param token The bracket.
	 */
	matchBracket(token: BraceToken): {
		match: Token | null;
		tokenIndex: number;
		erroneous: boolean;
	} {
		return this.matchingBrackets.get(token)!;
	}

	// Used to prevent infinite loops from crashing the parser
	private _fuel = 0;

	/**
	 * peek() implementation when we are in type mode, and need to split apart >>> tokens.
	 */
	_peekTypeModeImpl(): Token {
		const current = this.tokens[this.currentIndex]!;
		switch (current.type) {
			case Operator.GtEq:
			case Operator.GtGt:
			case Operator.GtGtEq:
			case Operator.GtGtGtEq:
			case Operator.GtGtGt: // needs splitting
				const tok = tokenTypeToString(current.type).charAt(this.splitIndex);
				const splitStart = current.span.start + this.splitIndex;
				return {
					type: tok === "=" ? Operator.Eq : Operator.Gt,
					span: {
						start: splitStart,
						end: splitStart + 1,
					},
				};
		}
		return current;
	}

	/**
	 * Get the current token.
	 */
	peek() {
		if (this._fuel++ > 1e6) {
			console.log("Stuck token:", this.tokens[this.currentIndex]);
			cafebabeAssert(false, "infinite loop");
		}
		if (this.typeMode) {
			return this._peekTypeModeImpl();
		}
		return this.tokens[this.currentIndex]!;
	}

	/**
	 * Look ahead `count` tokens. For example, if we're currently on the token 'a' in 'a' '(' 2, then ahead(1) is '('
	 * and ahead(2) is 2.
	 * @param count The lookahead count.
	 */
	ahead(count: number): Token {
		const i = this.currentIndex + count;
		if (i >= this.tokens.length) return this.tokens.at(-1)!;
		return this.tokens[i]!;
	}

	/**
	 * Step to the next token, in type mode (splitting >>> into > > >).
	 */
	private _nextTypeModeImpl() {
		const current = this.tokens[this.currentIndex]!;
		switch (current.type) {
			case Operator.GtEq:
			case Operator.GtGt:
			case Operator.GtGtEq:
			case Operator.GtGtGtEq:
			case Operator.GtGtGt:
				const len = tokenTypeToLength(current.type);
				let currentSplit = this.splitIndex;
				if (currentSplit < len - 1) {
					this.splitIndex = ++currentSplit;
					return;
				} else {
					this.splitIndex = 0;
				} // fallthrough to default implementation (finished with this >>> token, step to next)
		}
		if (this.currentIndex < this.tokens.length - 1) this.currentIndex++;
	}

	/**
	 * If we're currently splitting a token, and we're finished parsing a type, then any trailing characters in
	 * the token are invalid (i.e., there is no context where =, >=, or >>= follows a type context), so we move to
	 * the next token and mark the trailing characters as unexpected.
	 */
	private _exitTypeMode() {
		const split = this.splitIndex;
		if (split > 0) {
			const unsplitToken = this.tokens[this.currentIndex++]!;
			const span = unsplitToken.span;
			const token = tokenTypeToString(unsplitToken.type).slice(split);
			// Unexpected token '>='
			this.emitError(`Unexpected token '${token}'`, {
				start: span.start + split,
				end: span.end,
			});
			this.splitIndex = 0;
		}
		this.typeMode = 0;
	}

	/**
	 * Call this function at entry of any function that requires tokens like >> to be split into component > >, and
	 * call _endTypeSection() before returning from any such function.
	 */
	private _startTypeSection() {
		this.typeMode++;
	}

	/**
	 * Call this function at the exit of any function beginning in _startTypeSection().
	 */
	private _endTypeSection() {
		if (--this.typeMode === 0) {
			this._exitTypeMode();
		}
	}

	/**
	 * Step to the next token, saturating at the end of the file.
	 */
	next() {
		if (this.typeMode) this._nextTypeModeImpl();
		else if (this.currentIndex < this.tokens.length - 1) this.currentIndex++;
	}

	/**
	 * Whether we have reached the end of the file.
	 */
	eof(): boolean {
		return this.currentIndex >= this.tokens.length - 1;
	}

	/**
	 * Roll back the token stream.
	 * @param index Token index to move to. Usually used in conjunction with a previous value of `this.currentIndex`.
	 */
	rollback(index: number) {
		cafebabeAssert(
			index >= 0 && index < this.tokens.length,
			"Invalid rollback index"
		);
		this.currentIndex = index;
	}

	/**
	 * Emit a compilation error.
	 * @param message The compile error message.
	 * @param span The associated span of the error.
	 */
	emitError(message: string, span: {start: number; end: number}) {
		const last = this.errors.at(-1);
		if (last?.span.start !== span.start)
			// only emit a new error if it's at a new location
			this.errors.push(new CompileError(message, span));
	}

	/**
	 * Attempt to consume a token of a given type. Raise a syntax error if the token is not available. Unconditionally
	 * advance the token stream.
	 * @param type Type of the token to consume.
	 * @param advanceOnErr Whether to consume the token unconditionally.
	 * @param usePrevSpan Whether to use the end of the previous token's span as the error span.
	 * @param message Message to emit if the token is not available. The default message is "Expected <token type>".
	 */
	expectTT<T extends TokenType>(
		type: T,
		advanceOnErr = true,
		usePrevSpan = false,
		message?: () => string
	): TokenOfType<T> | null {
		const tok = this.peek();
		if (tok.type === type) {
			this.next();
			return tok as TokenOfType<T>;
		}
		if (advanceOnErr) this.next();
		let span = tok.span;
		if (usePrevSpan) {
			const prevToken = this.tokens[this.currentIndex - 1];
			if (prevToken) {
				span = {start: prevToken.span.end, end: prevToken.span.end + 1};
			}
		}
		this.emitError(message?.() ?? "Expected " + tokenTypeToString(type), span);
		return null;
	}

	expectSemicolon(): TokenOfType<Separator.Semicolon> | null {
		return this.expectTT(Separator.Semicolon, false, true);
	}

	/**
	 * Attempt to consume a token of a given type. Return null if the token is not available, but do not advance
	 * or raise an error.
	 * @param type Type of the token to consume.
	 */
	maybeTT<T extends TokenType>(type: T): TokenOfType<T> | null {
		const tok = this.peek();
		if (tok.type === type) {
			this.next();
			return tok as TokenOfType<T>;
		}
		return null;
	}

	/**
	 * Java has eight primitive types. Also, "void" is sometimes like a primitive type, for example in the return
	 * values of functions and in the expression `void.class`, but it cannot be used, e.g., as the element type of
	 * an array.
	 *
	 * PrimitiveType (excluding void):
	 *   int | long | short | byte | char | float | double | boolean
	 *
	 * @param allowVoid Also permit the "primitive type" void
	 */
	consumePrimitiveType(allowVoid = false): PrimitiveTypeTree | null {
		const tok = this.peek();
		this.next(); // unconditionally advance
		if (isPrimitive(tok.type) || (allowVoid && tok.type === Keyword.void)) {
			return new PrimitiveTypeTree(tok as TokenOfType<PrimitiveKw>);
		}
		this.emitError("Expected primitive type (e.g. int)", tok.span);
		return null;
	}

	/**
	 * PrimitiveTypeOrPrimitiveArrayType:
	 *   PrimitiveType [ '[' ']' ]*
	 */
	consumePrimitiveOrPrimitiveArrayType(
		allowVoid = false
	): PrimitiveTypeTree | ArrayTypeTree | null {
		const prim = this.consumePrimitiveType(allowVoid);
		if (prim === null) return null;
		const brackets =
			prim.type === Keyword.void
				? null /* void[] illegal */
				: this.consumeOptionalBrackets();
		if (brackets) return new ArrayTypeTree(prim, brackets);
		return prim;
	}

	/**
	 * Type arguments of a generic type or function invocation, including the outermost angle brackets.
	 *
	 * CONTRACT: Current token should be a < symbol.
	 */
	_consumeTypeArguments(): TypeArgumentsTree | null {
		this._startTypeSection(); // we wish to split up >> into > >
		const openingLt = this.expectTT(Operator.Lt);
		cafebabeAssert(openingLt !== null, "consumeTypeArguments precondition");

		let closingGt: TokenOfType<Operator.Gt> | null = null;
		const args: (
			| ReferenceTypeTree
			| WildcardTree
			| ArrayTypeTree
			| PrimitiveTypeTree
		)[] = [];
		const commas: TokenOfType<Separator.Comma>[] = [];

		argsLoop: while (!this.eof()) {
			const tok = this.peek();
			switch (tok.type) {
				// May start a type
				case Misc.identifier:
				case Keyword.int:
				case Keyword.char:
				case Keyword.byte:
				case Keyword.short:
				case Keyword.long:
				case Keyword.float:
				case Keyword.double:
				case Keyword.boolean:
				case Keyword.void: {
					const referenceType = this.consumePrimitiveOrReferenceType();
					if (referenceType) {
						args.push(referenceType);
						break;
					} else break argsLoop;
				}
				// Wildcard
				case Operator.Question: {
					const wildcard = this.consumeWildcard();
					if (wildcard) {
						args.push(wildcard);
						break;
					} else break argsLoop;
				}
				case Operator.Gt: {
					closingGt = tok as TokenOfType<Operator.Gt>;
					this.next();
					break argsLoop;
				}
				default:
					this.emitError(
						`Unexpected token '${tokenTypeToString(tok.type)}' in type arguments`,
						tok.span
					);
					break argsLoop;
			}

			const current = this.peek();
			if (current.type === Operator.Gt) {
				closingGt = current as TokenOfType<Operator.Gt>;
				this.next();
				break;
			}

			const comma = this.expectTT(Separator.Comma, false);
			if (comma) commas.push(comma);
		}

		this._endTypeSection();
		return new TypeArgumentsTree(openingLt, args, commas, closingGt);
	}

	/**
	 * ReferenceType:
	 *   (ClassOrInterfaceType | TypeVariable) [ Brackets ]
	 * (ignoring primitive arrays here)
	 */
	consumeReferenceType(
		allowArrays = true,
		allowDiamond = false
	): ReferenceTypeTree | ArrayTypeTree | WildcardTree | null {
		const ident = this.expectTT(Misc.identifier, false, true);
		if (ident === null) return null;

		let rt: WildcardTree | ReferenceTypeTree | ArrayTypeTree =
			new ReferenceTypeTree(null, null, new IdentTree(ident), null);

		main: while (!this.eof()) {
			const tok = this.peek();
			switch (tok.type) {
				case Separator.Dot:
					const after = this.ahead(1);
					if (after.type === Misc.identifier) {
						// Continue the reference type by adding an identifier on the RHS
						rt = new ReferenceTypeTree(rt, tok, new IdentTree(after), null);
						this.next();
						this.next();

						break;
					} else if (!SyntaxFacts.NotTrueFieldAccessRhs.has(after.type)) {
						// Add dummy empty identifier for completion purposes
						this.next();
						rt = new ReferenceTypeTree(
							rt,
							tok,
							IdentTree.zw(tok.span.end),
							null
						);
						this.emitError("Expected identifier", after.span);
					}

					break main;
				case Operator.Lt: // e.g. ArrayList<...
					let tyArgsTree: TypeArgumentsTree | null = null;
					if (allowDiamond) {
						// Diamond operator
						if (this.ahead(1).type === Operator.Gt) {
							tyArgsTree = new TypeArgumentsTree(
								this.expectTT(Operator.Lt)!,
								[],
								[],
								this.expectTT(Operator.Gt)!
							);
							this.next();
							break main;
						}
					}

					tyArgsTree = this._consumeTypeArguments()!;
					rt.typeArgs = tyArgsTree;

					if (this.peek().type === Separator.Dot) {
						continue;
					}

					if (this.peek().type !== Separator.LBracket) break main;
				// fallthrough to consume brackets
				case Separator.LBracket:
					if (!allowArrays) break main;

					const bracketsTree = this.consumeOptionalBrackets(false, true)!;
					if (bracketsTree) rt = new ArrayTypeTree(rt, bracketsTree);
					break main;
				default:
					break main;
			}
		}

		return rt;
	}

	consumePrimitiveOrReferenceType(
		allowAnnot = false,
		allowVoid = false,
		allowDiamond = false,
		allowWildcard = false
	): ReferenceTypeTree | ArrayTypeTree | PrimitiveTypeTree | null {
		const annotations = allowAnnot ? this.consumeOptionalAnnotationList() : [];
		const tok = this.peek();

		if (isPrimitive(tok.type) || (allowVoid && tok.type === Keyword.void)) {
			const prim = this.consumePrimitiveType(allowVoid)!;
			// Possibly an array type
			const brackets = this.consumeOptionalBrackets(allowAnnot, true);
			return (
				brackets ? new ArrayTypeTree(prim, brackets) : prim
			).attachAnnotations(annotations);
		}

		return (this.consumeReferenceType(true, allowDiamond)?.attachAnnotations(
			annotations
		) ?? null) as any;
	}

	/**
	 * Consume a sequence of paired brackets as the suffix of a one- or multidimensional array.
	 *
	 * @param maybeAnnotated Whether to permit annotations in this parse.
	 * @param disallowIndexing Disallow indexing brackets after an initial sequence of brackets, e.g. [][1].
	 */
	consumeOptionalBrackets(
		maybeAnnotated = false,
		disallowIndexing = true
	): BracketsTree | null {
		const annotations: AnnotationTree[][] = [];
		let currentAnnotations: AnnotationTree[] = [];
		const bracketPairs: {open: Token; close: Token | null}[] = [];

		main: while (!this.eof()) {
			const tok = this.peek();
			switch (tok.type) {
				case Separator.At:
					if (maybeAnnotated) {
						const annot = this.consumeAnnotation();
						if (annot) currentAnnotations.push(annot);
					}
					break;
				case Separator.LBracket:
					const ahead = this.ahead(1);
					if (disallowIndexing && ahead.type !== Separator.RBracket) {
						break main;
					}

					annotations.push(currentAnnotations);
					currentAnnotations = [];
					// Error behavior: [][][ will be parsed as three bracket pairs, with a missing token for the last
					this.next();
					const r = this.expectTT(Separator.RBracket);

					bracketPairs.push({open: tok, close: r});
					if (r === null) break main;
					continue; // already stepped
				default:
					break main;
			}

			this.next();
		}

		if (bracketPairs.length === 0) return null;
		return new BracketsTree(annotations, bracketPairs);
	}

	consumeOptionalAnnotationArgs(): BaseTree | null {
		const tok = this.peek();

		if (tok.type === Separator.LParen) {
			// For now just skip to RParen
			const matching = this.matchBracket(tok as any);
			this.rollback(matching.tokenIndex + 1);
		}

		return null;
	}

	/**
	 * Contract: Current token should be an @
	 */
	consumeAnnotation(): AnnotationTree | null {
		const atToken = this.peek() as Token & {type: Separator.At};
		cafebabeAssert(atToken.type === Separator.At, "Expected @");
		this.next();

		const ident = this.consumeQualifiedIdentifier(
			QualIdentMode.Normal,
			" annotation"
		);
		if (ident === null) return null;

		const args = this.consumeOptionalAnnotationArgs();
		return new AnnotationTree(atToken, ident, null, null, args);
	}

	/**
	 * Parse an annotation list, if one is present.
	 */
	consumeOptionalAnnotationList(): AnnotationTree[] {
		const annotations: AnnotationTree[] = [];
		while (
			this.peek().type === Separator.At &&
			this.ahead(1).type !== Keyword.interface
		) {
			const annot = this.consumeAnnotation();
			if (annot) annotations.push(annot);
		}
		return annotations;
	}

	synchronize(tokenSet: Set<Token["type"]>, complain = false) {
		while (!tokenSet.has(this.peek().type) && this.peek().type !== Misc.eof) {
			if (complain) this.emitError("Unexpected token", this.peek().span);
			this.next();
			complain = false;
		}
	}

	consumePlainIdentifier(partOf = ""): IdentTree | null {
		const tok = this.peek();
		if (tok.type === Misc.identifier) {
			this.next();
			return new IdentTree(tok);
		}
		this.emitError("Expected an identifier" + partOf, tok.span);
		return null;
	}

	/**
	 * QualifiedIdentifier:
	 *  Identifier {. Identifier}
	 *
	 * Error behavior: If there is a trailing dot, not followed by a keyword, it is consumed with a blank
	 * PlainIdentifier on the right, and an error is reported. This behavior is useful for completions.
	 *
	 * @param mode Depending on the mode, don't raise an error if certain tokens follow, e.g. a * corresponding to an
	 * star import declaration.
	 * @param partOf For nicer error messages -- describe what was trying to be parsed
	 */
	consumeQualifiedIdentifier(
		mode: QualIdentMode = QualIdentMode.Normal,
		partOf = ""
	): IdentTree | NameTree | null {
		let ident: IdentTree | NameTree | null =
			this.consumePlainIdentifier(partOf);
		if (ident === null) return null; // couldn't find anything at all

		while (this.peek().type === Separator.Dot) {
			const dotToken = this.expectTT(Separator.Dot)!;
			const makeDummyTree = () => {
				const dummyTreeStart = dotToken.span.start + 1;
				ident = new NameTree(dotToken, ident!, IdentTree.zw(dummyTreeStart));
			};

			const next = this.peek();
			if (
				mode & QualIdentMode.AllowTrailingStar &&
				next.type === Operator.Star
			) {
				makeDummyTree();
				break;
			} else if (
				mode === QualIdentMode.AllowTrailingForPrimary &&
				(next.type === Operator.Lt ||
					next.type === Keyword.super ||
					next.type === Keyword.class ||
					next.type === Keyword.this ||
					next.type === Keyword.new)
			) {
				this.rollback(this.currentIndex - 1); // step back
				break; // no dummy tree necessary
			}

			const nextIdent = this.consumePlainIdentifier(partOf);
			if (nextIdent === null) {
				makeDummyTree();
				break;
			}
			ident = new NameTree(dotToken, ident, nextIdent);
		}
		return ident;
	}

	// TODO add to model and implement
	_consumeAnnotationTypeDeclaration(): BaseTree | null {
		const start = this.currentIndex;

		const atToken = this.expectTT(Separator.At);
		if (atToken === null) return null;

		const interfaceToken = this.expectTT(Keyword.interface);
		if (interfaceToken === null) return null;

		const ident = this.consumePlainIdentifier(" annotation type");
		void ident;
		const openBrace = this.expectTT(Separator.LBrace);
		if (!openBrace) return null;

		const matching = this.matchBracket(openBrace);
		this.rollback(matching.tokenIndex + 1);
		return new ErrorTree(this.tokens.slice(start, this.currentIndex));
	}

	consumeClassOrInterfaceBody(className: string): BaseTree[] | null {
		const decls: BaseTree[] = [];
		while (!this.eof()) {
			const tok = this.peek();
			if (tok.type === Separator.RBrace) {
				break;
			}

			//console.log("Processing class body declaration", this.peek());

			const decl = this.consumeClassBodyDeclaration(className);
			if (decl === null) {
				// parserAssert(false, "wtf");
				this.synchronize(SyntaxFacts.StartImportOrTypeDeclOrPackage); // TODO
				continue;
			}
			decls.push(decl);
		}

		return decls;
	}

	/**
	 * LabelledStatement:
	 *   Identifier ':' Statement
	 */
	consumeLabelledStatement(): LabelledStatementTree | null {
		const label = this.consumePlainIdentifier(" labelled statement");
		if (label === null) return null;

		const colon = this.expectTT(Operator.Colon);
		const stmt = this.consumeStatement();

		return new LabelledStatementTree(label, colon, stmt);
	}

	/**
	 * Statement:
	 *   LabelledStatement   (e.g. l: ...)
	 *   AssignmentStatement (e.g.  a += 2)
	 *   ExpressionStatement (e.g.  a.cow())
	 */
	consumeStatement(): BaseTree | null {
		const tok = this.peek();

		switch (tok.type) {
			case Separator.LBrace:
				return this.consumeBlock();
			case Keyword.if:
				return this.consumeIfStatement();
			case Keyword.for:
				return this.consumeForOrEnhancedForStatement();
			case Keyword.while:
				return this.consumeWhileStatement();
			case Keyword.do:
				return this.consumeDoWhileStatement();
			case Keyword.try:
				return this.consumeTryStatement();
			case Keyword.return:
				return this.consumeReturnStatement();
			case Keyword.break:
				return this.consumeBreakStatement();
			case Keyword.continue:
				return this.consumeContinueStatement();
			case Keyword.throw:
				return this.consumeThrowStatement();
			case Keyword.assert:
				return this.consumeAssertStatement();
			case Keyword.synchronized:
				return this.consumeSynchronizedStatement();
			case Keyword.switch:
				return this.consumeSwitchStatement();
			case Separator.Semicolon:
				return this.consumeEmptyStatement();
			default:
				if (
					tok.type === Misc.identifier &&
					this.ahead(1).type === Operator.Colon
				) {
					return this.consumeLabelledStatement();
				}
				return this.consumeExpressionStatement(true);
		}
	}

	consumeExpressionStatement(
		includeSemi: boolean
	): ExpressionStatementTree | null {
		const errorCount = this.errors.length;

		const expr = this.consumeExpression();
		if (!expr) return null;

		const expressionStatement = new ExpressionStatementTree(
			expr,
			includeSemi ? this.expectSemicolon() : null
		);

		if (this.errors.length === errorCount)
			// no errors occurred when parsing the statement, so check it
			this._checkValidExpressionStatement(expr);
		return expressionStatement;
	}

	/**
	 * The set of expressions that can be on a standalone line with a semicolon is restricted to assignments, unary
	 * ++/--, method invocation and class creations.
	 *
	 * For example, "3;" and "4 + 3;" are invalid, but "x++;" and "new Cow();" are valid expression statements.
	 */
	_checkValidExpressionStatement(stmt: BaseExpressionTree) {
		if (
			stmt instanceof AssignmentTree ||
			(stmt instanceof UnaryOperatorTree &&
				(stmt.operatorTag === Operator.PlusPlus ||
					stmt.operatorTag === Operator.MinusMinus)) ||
			stmt instanceof MethodInvocationTree ||
			stmt instanceof ClassCreationTree ||
			stmt instanceof AnonymousClassCreationTree
		) {
			return;
		}

		stmt.resolveSpans();
		this.emitError("Invalid expression statement", stmt.span);
	}

	/**
	 * Consume the '(' Expression ')' in if-statements, while-statements, etc.
	 *
	 * @param partOf For error messages, e.g. "if-condition"
	 */
	_consumeParenthesizedCondition(partOf: string): {
		openParen: TokenOfType<Separator.LParen> | null;
		condition: BaseExpressionTree | null;
		closeParen: TokenOfType<Separator.RParen> | null;
	} {
		const openParen = this.expectTT(
			Separator.LParen,
			false,
			false,
			() => "Expected ( for start of " + partOf
		);
		const condition = this.consumeExpression(" for " + partOf);
		const closeParen = this.expectTT(
			Separator.RParen,
			false,
			false,
			() => "Expected ) for end of " + partOf
		);

		return {openParen, condition, closeParen};
	}

	/**
	 * IfStatement:
	 *   if '(' Expression ')' Statement [else Statement]
	 *
	 * Note that chained if-else expressions are just normal if statements, with the second Statement itself being
	 * an if statement.
	 */
	consumeIfStatement(): IfStatementTree | null {
		const ifToken = this.expectTT(Keyword.if);
		if (ifToken === null) return null;

		const {openParen, condition, closeParen} =
			this._consumeParenthesizedCondition("if-condition");
		const thenStatement = this.consumeStatement();

		const elseToken = this.maybeTT(Keyword.else);
		const elseStatement = elseToken ? this.consumeStatement() : null;

		return new IfStatementTree(
			ifToken,
			openParen,
			condition,
			closeParen,
			thenStatement,
			elseToken,
			elseStatement
		);
	}

	/**
	 * Comma-separated arguments to a method. Each argument should be an expression.
	 *
	 * TODO: use expectedEnd for error recovery
	 * @param expectedEnd
	 */
	consumeMethodInvocationArguments(expectedEnd: Token | null): {
		exprs: BaseExpressionTree[];
		commas: Token[];
	} {
		const exprs: BaseExpressionTree[] = [];
		const commas: Token[] = [];

		while (true) {
			if (this.peek().type === Separator.RParen) {
				break;
			}

			const expr = this.consumeExpression();
			if (expr === null) break;

			exprs.push(expr);

			if (this.peek().type === Separator.Comma) {
				commas.push(this.expectTT(Separator.Comma)!);
			} else {
				break;
			}
		}

		return {exprs, commas};
	}

	/**
	 * Consume things that follow the beginning of a primary expression. The permitted continuations are given by
	 * the flags field. Here are all the possibilities:
	 *
	 * Array access:
	 *   '[' Expression ']'
	 * Field access:
	 *   '.' Identifier
	 *   '.' 'super'
	 * This access:
	 *   '.' 'this'
	 * Super access:
	 *   '.' 'super'
	 * Immediate method invocation: (i.e., using the primary directly to invoke something)
	 *   '(' [ArgumentList] ')'
	 * Qualified method invocation:
	 *   '.' [TypeArguments] Identifier ( [ArgumentList] )
	 * Method reference:
	 *   '::' [TypeArguments] Identifier
	 *   '::' [TypeArguments] 'new'
	 */
	consumePrimaryExpressionRest(
		expr: BaseTree,
		permittedExprRestFlags: number
	): BaseExpressionTree | null {
		const tok = this.peek();

		if (
			permittedExprRestFlags & ExprRest.ImmediateInvocation &&
			tok.type === Separator.LParen
		) {
			//  ( [ArgumentList ])
			// Attempt to "rip out" an identifier and/or dot token from expr as the method invocation tree
			// must contain the identifier
			let exprLeft: BaseExpressionTree | null = null;
			let dotToken: Token | null = null;
			let lhs:
				| IdentTree
				| ThisTree
				| SuperTree
				| ArrayCreationTree
				| ClassCreationTree
				| AnonymousClassCreationTree
				| null = null;

			if (
				expr instanceof IdentTree ||
				expr instanceof SuperTree ||
				expr instanceof ThisTree
			) {
				exprLeft = null;
				lhs = expr;
			} else if (expr instanceof NameTree) {
				exprLeft = expr.qualifier;
				dotToken = expr.dotToken;
				lhs = expr.identifier;
			} else if (expr instanceof FieldAccessTree) {
				exprLeft = expr.lhs;
				dotToken = expr.dotToken;
				lhs = expr.rhs!;
			} else {
				expr?.resolveSpans();
				this.emitError("This expression is not callable", expr.span);
			}

			const left = this.expectTT(Separator.LParen)!;
			const expectedEnd = this.matchBracket(left);
			const args = this.consumeMethodInvocationArguments(expectedEnd.match);
			const right = this.expectTT(Separator.RParen, false, true);

			return this.consumePrimaryExpressionRest(
				new MethodInvocationTree(
					exprLeft,
					dotToken,
					lhs,
					left,
					args.exprs,
					args.commas,
					right
				),
				ExprRest.All
			);
		}

		// Class literal?
		if (
			permittedExprRestFlags & ExprRest.ClassLiteral &&
			tok.type === Separator.Dot
		) {
			if (this.ahead(1).type === Keyword.class) {
				const dotToken = this.expectTT(Separator.Dot)!;
				const classToken = this.expectTT(Keyword.class)!;
				// Can follow a class with qualified invocation (e.g. int.class.getName()), field access (although
				// Class<?> has no fields, so this is useless) and method reference (e.g. int.class::new)

				if (
					!(
						expr.isTypeTree() ||
						expr instanceof IdentTree ||
						expr instanceof NameTree
					)
				) {
					this.emitError("Invalid class literal", expr.span);
					return new ErrorTree([expr, dotToken, classToken]);
				}

				return this.consumePrimaryExpressionRest(
					new ClassLiteralTree(
						expr as
							| ArrayTypeTree
							| PrimitiveTypeTree
							| ReferenceTypeTree
							| IdentTree,
						dotToken,
						classToken
					),
					ExprRest.QualifiedInvocation |
						ExprRest.FieldAccess |
						ExprRest.MethodReference
				);
			}
		}

		if (
			permittedExprRestFlags & ExprRest.QualifiedInvocation &&
			tok.type === Separator.Dot
		) {
			if (this.ahead(1).type === Operator.Lt) {
				const dotToken = this.expectTT(Separator.Dot)!;
				const tyArgs = this._consumeTypeArguments();
				const ident = this.consumePlainIdentifier(" method invocation");
				const openParen = this.expectTT(Separator.LParen)!;
				const args = this.consumeMethodInvocationArguments(null);
				return this.consumePrimaryExpressionRest(
					new MethodInvocationTree(
						expr as BaseExpressionTree,
						dotToken,
						ident,
						openParen,
						args.exprs,
						args.commas,
						this.expectTT(Separator.RParen, false, true)!,
						tyArgs
					),
					ExprRest.All
				);
			} else if (
				this.ahead(1).type === Misc.identifier &&
				this.ahead(2).type === Separator.LParen
			) {
				// Qualified method invocation
				const dotToken = this.expectTT(Separator.Dot)!;
				const ident = this.consumePlainIdentifier(" method invocation")!;
				const openParen = this.expectTT(Separator.LParen)!;
				const args = this.consumeMethodInvocationArguments(null);
				return this.consumePrimaryExpressionRest(
					new MethodInvocationTree(
						expr as BaseExpressionTree,
						dotToken,
						ident,
						openParen,
						args.exprs,
						args.commas,
						this.expectTT(Separator.RParen, false, true)!
					),
					ExprRest.All
				);
			}
		}

		if (
			permittedExprRestFlags & ExprRest.ArrayAccess &&
			tok.type === Separator.LBracket
		) {
			// Array access
			const openBracket = this.expectTT(Separator.LBracket)!;
			const index = this.consumeExpression();
			const closeBracket = this.expectTT(Separator.RBracket)!;

			return this.consumePrimaryExpressionRest(
				new ArrayAccessTree(
					expr as BaseExpressionTree,
					openBracket,
					index,
					closeBracket
				),
				ExprRest.All
			);
		}

		if (
			permittedExprRestFlags & ExprRest.ThisAccess &&
			tok.type === Separator.Dot &&
			this.ahead(1).type === Keyword.this
		) {
			const t = new FieldAccessTree(
				expr as BaseExpressionTree,
				this.expectTT(Separator.Dot)!,
				new ThisTree(this.peek() as any)
			);
			this.next();
			return this.consumePrimaryExpressionRest(t, ExprRest.All);
		}

		if (
			permittedExprRestFlags & ExprRest.SuperAccess &&
			tok.type === Separator.Dot &&
			this.ahead(1).type === Keyword.super
		) {
			const t = new FieldAccessTree(
				expr as BaseExpressionTree,
				this.expectTT(Separator.Dot)!,
				new SuperTree(this.peek() as any)
			);
			this.next();
			return this.consumePrimaryExpressionRest(t, ExprRest.All);
		}

		if (
			permittedExprRestFlags & ExprRest.NewExpression &&
			tok.type === Separator.Dot &&
			this.ahead(1).type === Keyword.new
		) {
			const dotToken = this.expectTT(Separator.Dot)!;

			const newExpr = this.consumePrimaryNewExpression();
			return this.consumePrimaryExpressionRest(
				new MethodInvocationTree(
					expr as BaseExpressionTree,
					dotToken,
					newExpr,
					null,
					[],
					[],
					null
				),
				ExprRest.All
			);
		}

		if (
			permittedExprRestFlags & ExprRest.FieldAccess &&
			tok.type === Separator.Dot
		) {
			// Field access
			const dotToken = this.expectTT(Separator.Dot)!;
			const ident = this.consumePlainIdentifier(" field access");

			return this.consumePrimaryExpressionRest(
				new FieldAccessTree(expr as BaseExpressionTree, dotToken, ident),
				ExprRest.All
			);
		}

		if (
			permittedExprRestFlags & ExprRest.MethodReference &&
			tok.type === Separator.ColonColon
		) {
			const doubleColon = this.expectTT(Separator.ColonColon)!;
			const typeArgs =
				this.peek().type === Operator.Lt ? this._consumeTypeArguments() : null;
			const ident =
				this.maybeTT(Keyword.new) ??
				this.consumePlainIdentifier(" method reference");

			return new MethodReferenceTree(
				expr as BaseExpressionTree,
				doubleColon,
				typeArgs,
				ident
			); // stuff after reference is disallowed
		}

		if (!(expr instanceof BaseExpressionTree)) {
			// TODO consider what to do here
			return new ErrorTree([expr]);
		}

		return expr as BaseExpressionTree; // Couldn't read anything on the RHS
	}

	/**
	 * A primary expression is anything binding tighter than operators. For example, new ArrayList<>() and
	 * System.out.println("cow") are all primary expressions, while a + b is not (it is a binary operator expression,
	 * each composed of two primary expressions).
	 *
	 * There are obviously a tonne of different primary expressions, so this function is a little messy, although
	 * we try to delegate things out. Most primary expressions can be continued, e.g. with dot accesses; this is
	 * delegated to consumePrimaryExpressionRest, which is recursive.
	 *
	 * As an example, new ArrayList<>().get(0).type is a primary expression -- specifically, a MethodInvocationTree.
	 * Ultimately, this example is parsed as follows:
	 *   consumePrimaryExpression
	 *     └── token 'new' : consumePrimaryNewExpression -> ClassCreationTree "new ArrayList<>()"
	 *     ┌────┘
	 *     └── token '.'   : consumePrimaryExpressionRest -> MethodInvocationTree "new ArrayList<>().get(0)"
	 *     ┌────┘
	 *     └── token '.'   : consumePrimaryExpressionRest -> FieldAccessTree "new ArrayList<>().get(0).type"
	 *     ┌────┘
	 *     └── EOF: done!
	 *
	 * @param reason The reason a primary expression was expected.
	 * @returns An expression tree, or null if one could not be parsed.
	 */
	consumePrimaryExpression(reason = ""): BaseExpressionTree | null {
		const tok = this.peek();
		let continuationFlags: ExprRest = 0;
		let expr: BaseTree | null = null;

		switch (tok.type) {
			// Literals (true, null, strings, chars, etc.)
			case Misc.booleanLiteral:
				this.next();
				return new BooleanLiteralTree(tok);
			case Misc.floatLiteral:
				this.next();
				return new FloatLiteralTree(tok);
			case Misc.integer:
				this.next();
				return new IntegerLiteralTree(tok);
			case Misc.null:
				this.next();
				return new NullLiteralTree(tok);
			case Misc.characterLiteral:
				this.next();
				return new CharacterLiteralTree(tok);
			// In the case of a string, only a few things may follow (e.g. "cow".length())
			case Misc.string:
				continuationFlags =
					ExprRest.FieldAccess |
					ExprRest.NewExpression |
					ExprRest.QualifiedInvocation;
				expr = new StringLiteralTree(this.expectTT(Misc.string)!);
				break;

			// New expressions (class, array, etc.)
			case Keyword.new:
				expr = this.consumePrimaryNewExpression();
				continuationFlags = ExprRest.All;
				break;
			case Keyword.this:
				expr = new ThisTree(this.expectTT(Keyword.this)!);
				continuationFlags = ExprRest.All;
				break;
			case Keyword.super:
				expr = new SuperTree(this.expectTT(Keyword.super)!);
				continuationFlags = ExprRest.All;
				break;

			case Keyword.int:
			case Keyword.char:
			case Keyword.byte:
			case Keyword.short:
			case Keyword.long:
			case Keyword.float:
			case Keyword.double:
			case Keyword.boolean:
			case Keyword.void: {
				// Only possibility is that it's a ClassLiteral for a primitive or an array type
				expr = this.consumePrimitiveOrPrimitiveArrayType(true /* allow void */);
				continuationFlags = ExprRest.ClassLiteral; // e.g. int.class
				if (expr instanceof ArrayTypeTree)
					continuationFlags |= ExprRest.MethodReference; // e.g. int[]::new
				break;
			}

			case Separator.LParen: {
				// '(' Expression ')'  -> parenthesized expression
				this.next();
				const inner = this.consumeExpression();
				const rparen = this.expectTT(Separator.RParen, false, true);
				expr = new ParenthesizedExpressionTree(tok, inner, rparen);
				continuationFlags = ExprRest.All;
				break;
			}

			case Misc.identifier: {
				if (this.ahead(1).type === Operator.Arrow) {
					expr = this.consumeLambda(
						this.ahead(1) as TokenOfType<Operator.Arrow>
					);
					continuationFlags = ExprRest.None;
					break;
				}

				const saveState = this.currentIndex;
				const qual = this.consumeQualifiedIdentifier(
					QualIdentMode.AllowTrailingForPrimary
				)!;
				const afterQualState = this.currentIndex;

				// Issue: ArrayList<Integer>::new
				//                 ^ we are here -- needs to be distinguished from a comparison. (ugh.)
				// e.g. a.length < 1
				const following = this.peek();
				const isArrayTy =
					following.type === Separator.LBracket &&
					this.ahead(1).type === Separator.RBracket;
				if (following.type === Operator.Lt || isArrayTy) {
					// Attempt to parse as a reference type followed by ::
					this.rollback(saveState);
					const {errs, result: refType} = this.captureErrors(() =>
						this.consumePrimitiveOrReferenceType()
					);
					const followingType = this.peek();
					if (
						followingType.type === Separator.ColonColon &&
						errs.length === 0
					) {
						expr = refType;
						continuationFlags = ExprRest.MethodReference;
						break;
					} else if (
						followingType.type === Separator.Dot &&
						errs.length === 0
					) {
						if (!isArrayTy)
							// Illegal use of a templated type, e.g. ArrayList<Integer>.cow, but just let it go
							this.emitError(
								"Generic type not allowed here",
								followingType.span
							);
						expr = refType;
						continuationFlags = ExprRest.FieldAccess | ExprRest.ClassLiteral;
						break;
					}

					this.rollback(afterQualState);
					// nothing left to do here -- ultimately parse as a comparison operator
				}

				expr = qual;
				continuationFlags = ExprRest.All;
				break;
			}

			default:
				this.emitError(
					"Expected the start of an expression" + reason,
					tok.span
				);
		}

		if (!expr) return null;

		return this.consumePrimaryExpressionRest(expr, continuationFlags);
	}

	/**
	 * NewExpression:
	 *   ArrayCreationExpression
	 *   ClassInstanceCreationExpression
	 */
	consumePrimaryNewExpression():
		| AnonymousClassCreationTree
		| ClassCreationTree
		| ArrayCreationTree
		| null {
		const newToken = this.expectTT(Keyword.new);
		if (newToken === null) return null;

		let ctorTypeArgs: TypeArgumentsTree | null = null;
		if (this.peek().type === Operator.Lt)
			ctorTypeArgs = this._consumeTypeArguments();
		const type = this.consumePrimitiveOrReferenceType(
			false,
			false,
			false,
			true
		) as ArrayTypeTree | ReferenceTypeTree | null;
		if (type === null)
			return new ClassCreationTree(newToken, null, null, [], [], null, null);

		if (this.peek().type === Separator.LParen) {
			// ClassInstanceCreationExpression
			const openParen = this.expectTT(Separator.LParen);
			const args = this.consumeMethodInvocationArguments(
				openParen ? this.matchBracket(openParen).match : null
			);
			const closeParen = this.expectTT(Separator.RParen, false, true);

			if (this.peek().type === Separator.LBrace) {
				// AnonymousClassCreationTree
				const openBrace = this.expectTT(Separator.LBrace)!;
				const body = this.consumeClassOrInterfaceBody("AnonymousClass");
				const closeBrace = this.expectTT(Separator.RBrace);

				if (!(type instanceof ReferenceTypeTree))
					this.emitError(
						"Anonymous class creation not allowed for primitive or array types",
						type.span
					);

				return new AnonymousClassCreationTree(
					newToken,
					type,
					ctorTypeArgs,
					args.exprs,
					body,
					openBrace,
					closeBrace
				);
			}

			return new ClassCreationTree(
				newToken,
				type,
				ctorTypeArgs,
				args.exprs,
				args.commas,
				openParen,
				closeParen
			);
		} else if (type instanceof ArrayTypeTree) {
			if (this.peek().type === Separator.LBrace) {
				return new ArrayCreationTree(
					newToken,
					type,
					[],
					[],
					this.consumeArrayInitializer()
				);
			}
		}

		if (this.peek().type === Separator.LBracket) {
			// ArrayCreationExpression
			const args: BaseExpressionTree[] = [];
			const dimsBrackets: TokenOfType<
				Separator.LBracket | Separator.RBracket
			>[] = [];

			while (this.peek().type === Separator.LBracket) {
				dimsBrackets.push(this.expectTT(Separator.LBracket)!);
				let closeBracket = this.maybeTT(Separator.RBracket);
				if (closeBracket) {
					dimsBrackets.push(closeBracket);
					continue;
				}

				const expression = this.consumeExpression();
				closeBracket = this.expectTT(Separator.RBracket);
				if (closeBracket) dimsBrackets.push(closeBracket);
				if (expression) args.push(expression);
			}

			return new ArrayCreationTree(
				newToken,
				type,
				dimsBrackets,
				args,
				this.peek().type === Separator.RBracket
					? this.consumeArrayInitializer()
					: null
			);
		} else {
			this.emitError("Expected ( or [ after new", this.peek().span);
		}

		return new ClassCreationTree(
			newToken,
			type,
			ctorTypeArgs,
			[],
			[],
			null,
			null
		);
	}

	/**
	 * Consume the arguments of a parenthesized lambda and push them into args. Return the starting and ending
	 * parentheses.
	 *
	 * TODO: Better error recovery for malformed arguments.
	 *
	 * (int a, int b)
	 * ^ WE ARE HERE
	 *
	 * @param args
	 */
	private _consumeLambdaArguments(
		args: FunctionArgumentTree[]
	): [TokenOfType<Separator.LParen>, TokenOfType<Separator.RParen> | null] {
		const lparen = this.expectTT(Separator.LParen)!;
		cafebabeAssert(lparen !== null, "consumeLambdaArguments precondition");

		// e.g. (a, b) ->, no types on variables
		//       ^
		const ahead = this.ahead(1).type;
		const isElidedTypeLam =
			ahead === Separator.Comma || ahead === Separator.RParen;

		while (true) {
			const tok = this.peek();
			if (tok.type === Separator.RParen)
				// done consuming arguments
				break;

			const finalToken = isElidedTypeLam ? null : this.maybeTT(Keyword.final); // e.g. (final int a) -> a
			const type = isElidedTypeLam
				? null
				: this.consumePrimitiveOrReferenceType(false, false, false, true);

			if (!isElidedTypeLam && this._guessIsMissingTypeForVariable(type)) break;

			const ident = this.consumePlainIdentifier(" for variable name");
			if (ident === null) break;

			args.push(new FunctionArgumentTree(type, finalToken, ident, null, null));

			if (this.peek().type === Separator.Comma) {
				this.next();
			} else {
				break;
			}
		}

		const rparen = this.expectTT(Separator.RParen, false, true);
		return [lparen, rparen];
	}

	/**
	 * Consume the part of a lambda expression following the arrow token.
	 *
	 * LambdaBody:
	 *  Block           (e.g. in: a -> { return a; })
	 *  Expression      (e.g. in: a -> a * 2)
	 */
	consumeLambdaBody(): BlockTree | BaseExpressionTree | null {
		const tok = this.peek();
		if (tok.type === Separator.LBrace) {
			return this.consumeBlock();
		} else {
			return this.consumeExpression();
		}
	}

	/**
	 * Consume a lambda expression, optionally expecting to use the given arrow token in the process.
	 * @param expectedArrowToken The arrow token.
	 */
	consumeLambda(
		expectedArrowToken: (Token & {type: Operator.Arrow}) | null = null
	): LambdaExpressionTree | null {
		const tok = this.peek();
		const args: FunctionArgumentTree[] = [];

		let lparen: TokenOfType<Separator.LParen> | null = null;
		let rparen: TokenOfType<Separator.RParen> | null = null;
		let arrow: TokenOfType<Operator.Arrow> | null = null;

		if (tok.type === Separator.LParen) {
			[lparen, rparen] = this._consumeLambdaArguments(args);
		} else if (tok.type === Misc.identifier) {
			const ident = this.consumePlainIdentifier()!;
			if (ident === null) return null;
			args.push(new FunctionArgumentTree(null, null, ident, null, null));
		} else {
			this.emitError("Expected lambda arguments", tok.span);
			return null;
		}

		arrow = this.expectTT(Operator.Arrow);
		if (arrow === null) return null;

		const body = this.consumeLambdaBody();
		return new LambdaExpressionTree(args, lparen, rparen, arrow, body);
	}

	/**
	 * Get a token of the given type at a given index, throwing an error if it is not found. Useful for internal
	 * assertions.
	 */
	private _getTokenOfType<T extends Token["type"]>(
		index: number,
		type: T,
		failure: string
	): TokenOfType<T> {
		const tok = this.tokens[index]!;
		cafebabeAssert(tok && tok.type === type, failure);
		return tok as TokenOfType<T>;
	}

	/**
	 * Check whether the token is probably the starting parenthesis of a parenthesized lambda expression, for example
	 * (a, b) -> 2. This is used to differentiate lambdas from other expressions ahead of time.
	 *
	 * Contract: The given token should be a '('.
	 *
	 * @param tokenOffset The index of the start token to check.
	 * @returns The arrow token associated with the lambda, or null if a lambda expression was not detected.
	 */
	private _probablyParenthesizedLambdaDecl(
		tokenOffset: number
	): TokenOfType<Operator.Arrow> | null {
		const {match, tokenIndex} = this.matchBracket(
			this._getTokenOfType(
				tokenOffset,
				Separator.LParen,
				"probablyParenthesizedLambdaDecl precondition"
			)
		);
		if (match) {
			// Check whether token following the ')' is an arrow
			const maybeArrow = this.tokens[tokenIndex + 1]!;
			if (maybeArrow.type === Operator.Arrow)
				return maybeArrow as TokenOfType<Operator.Arrow>;
		}
		return null;
	}

	/**
	 * Consume optional bounds on a type.
	 *
	 * OptionalBounds:
	 *   { '&' InterfaceType }
	 *
	 * Additional bounds are used in generics and generic casts to restrict a type.
	 */
	_consumeOptionalBounds(): BoundsTree | null {
		const ampersands: (Token & {type: Operator.And})[] = [];
		const interfaces: ReferenceTypeTree[] = [];
		let a: (Token & {type: Operator.And}) | null = null;
		while ((a = this.maybeTT(Operator.And))) {
			ampersands.push(a);
			const ty = this.consumePrimitiveOrReferenceType(
				false,
				false,
				false,
				false
			);
			if (ty instanceof ArrayTypeTree || ty instanceof PrimitiveTypeTree) {
				// e.g. Cow & int[]
				this.emitError(
					"Primitive and array types are not allowed in bounds",
					ty.span
				);
			} else if (ty === null) {
				this.emitError("Expected interface name", this.peek().span);
				this.next();
			} else {
				interfaces.push(ty as ReferenceTypeTree);
			}
		}
		return ampersands.length > 0
			? new BoundsTree(ampersands, interfaces)
			: null;
	}

	/**
	 * Either a primitive cast:
	 *   '(' int | boolean | ... | long ')' UnaryExpression
	 * Or a reference cast:
	 *   (ReferenceType {Bounds}) UnaryExpressionNotPlusMinus
	 *   (ReferenceType {Bounds}) LambdaExpression
	 */
	consumeCast(): TypeCastTree | null {
		const openParen = this.expectTT(Separator.LParen);
		if (openParen === null) return null;
		const referenceType = this.consumePrimitiveOrReferenceType(
			false,
			false,
			false,
			true
		);
		if (referenceType === null) return null;
		const additionalBounds = this._consumeOptionalBounds();
		return new TypeCastTree(
			openParen,
			referenceType as ReferenceTypeTree,
			additionalBounds,
			this.expectTT(Separator.RParen, false, true),
			this.consumeExpression()
		);
	}

	/**
	 * Create a binary operation from the given token and the given left and right-hand sides.
	 */
	private _makeBinOp(
		op: TokenOfType<BinaryOperator>,
		lhs: BaseExpressionTree,
		rhs: BaseExpressionTree | null
	): BaseExpressionTree | null {
		return new BinaryOperatorTree(op, lhs, rhs);
	}

	/**
	 * Make a (compound) assignment operation, e.g. +=, =, *=, from the right-hand side to the left-hand side.
	 */
	private _makeAssignmentOp(
		op: AssignmentOrCompoundAssignment,
		lhs: BaseExpressionTree,
		rhs: BaseExpressionTree | null
	): BaseExpressionTree | null {
		return new AssignmentTree(lhs, op, rhs);
	}

	/**
	 * Whether the given expression is a syntactically valid left-hand assignment. For example, it is syntactically
	 * valid to assign to "cow.pox", but not to "cow.pox()" or "2".
	 */
	static _validAssignmentLhs(primary: BaseExpressionTree) {
		return (
			primary instanceof FieldAccessTree ||
			primary instanceof ArrayAccessTree ||
			primary instanceof NameTree ||
			primary instanceof IdentTree
		);
	}

	/**
	 * Consume a top-level expression.
	 * @param reason The reason an expression was expected, for error reporting purposes.
	 */
	consumeExpression(reason = ""): BaseExpressionTree | null {
		const tok = this.peek();

		// Detect lambdas
		switch (tok.type) {
			case Separator.LParen: {
				// Lambda w/ parentheses, e.g. (int a) -> a, just do this lol
				const targetArrowToken = this._probablyParenthesizedLambdaDecl(
					this.currentIndex
				);
				if (targetArrowToken) return this.consumeLambda(targetArrowToken);
				break;
			}
			case Misc.identifier: {
				// Lambda w/o parentheses and a single argument, e.g. a -> a
				const ahead = this.ahead(1);
				if (ahead.type === Operator.Arrow)
					return this.consumeLambda(ahead as Token & {type: Operator.Arrow});
			}
		}

		const lhs = this.consumeConditionalExpression(reason);
		if (lhs && isAssignmentOrCompoundAssignment(this.peek().type as Operator)) {
			if (!Parser._validAssignmentLhs(lhs)) {
				const lhsStart = tok.span.start;
				const lhsEnd = this.ahead(-1).span.end;

				this.emitError("Invalid left-hand side of assignment", {
					start: lhsStart,
					end: lhsEnd,
				});
			}

			const op = this.peek();
			this.next();

			const rhs = this.consumeExpression(reason);
			return this._makeAssignmentOp(
				op as AssignmentOrCompoundAssignment,
				lhs,
				rhs
			);
		} else {
			return lhs;
		}
	}

	consumeConditionalExpression(reason = ""): BaseExpressionTree | null {
		// Conditional expressions are at their own level; repeatedly parse a lower-precedence expression and then
		// look for a ? : sequence

		let lhs = this.consumePrimaryOrUnaryExpression(reason);
		if (lhs === null) return null;

		lhs = this.consumeExpressionOperatorPrecedence(lhs, 0, reason);

		while (this.peek().type === Operator.Question) {
			const question = this.expectTT(Operator.Question)!;
			const thenBranch = this.consumeExpression();
			const colon = this.expectTT(Operator.Colon)!;
			const elseBranch = this.consumeConditionalExpression();

			lhs = new TernaryOperatorTree(
				lhs,
				question,
				thenBranch,
				colon,
				elseBranch
			);
		}

		return lhs;
	}

	/**
	 * Expression with lower precedence than a conditional, which is at a sufficiently low level of precedence that we
	 * can use a straightforward operator precedence parser instead of insane nesting.
	 *
	 * @param lhs The initial primary expression.
	 * @param minPrecedence The minimum precedence of the current expression
	 * @param reason The reason an expression was expected.
	 */
	consumeExpressionOperatorPrecedence(
		lhs: BaseExpressionTree,
		minPrecedence: number,
		reason = ""
	): BaseExpressionTree | null {
		const exprStack: (BaseTree | null)[] = [lhs];
		const os: Token[] = []; // operator stack

		function binaryPrecedence(tok: Token["type"]): number {
			return (BinaryPrecedenceTable as any)[tok] ?? -1;
		}

		let tok: Token;
		while (binaryPrecedence((tok = this.peek()).type) >= minPrecedence) {
			os.push(tok);

			this.next();
			const expr =
				tok.type === Keyword.instanceof
					? this.consumePrimitiveOrReferenceType()
					: this.consumePrimaryOrUnaryExpression(reason);
			if (tok.type === Keyword.instanceof && expr instanceof PrimitiveTypeTree)
				this.emitError(
					"Right-hand side of instanceof must be a reference type",
					tok.span
				);

			exprStack.push(expr);

			// Credit: Wikipedia article
			while (
				os.length > 0 &&
				binaryPrecedence(os.at(-1)!.type) >= binaryPrecedence(this.peek().type)
			) {
				const exprRhs = exprStack.pop() ?? null;
				const op = os.pop()!;
				const exprLhs = exprStack.pop() ?? null;
				exprStack.push(
					new BinaryOperatorTree(
						op as TokenOfType<BinaryOperator>,
						exprLhs as BaseExpressionTree,
						exprRhs as BaseExpressionTree | ArrayTypeTree | ReferenceTypeTree
					)
				);
			}
		}

		return exprStack[0]! as BaseExpressionTree;
	}

	/**
	 * In Java, unary expressions always bind tighter than binary expressions, so this is very useful.
	 *
	 * @param reason The reason an expression was expected.
	 * @return An expression tree, or null if one could not be parsed.
	 */
	consumePrimaryOrUnaryExpression(reason = ""): BaseExpressionTree | null {
		const tok = this.peek();

		switch (tok.type) {
			case Operator.PlusPlus:
			case Operator.MinusMinus:
			case Operator.Plus:
			case Operator.Minus:
			case Operator.Not:
			case Operator.Tilde:
				this.next();
				return new UnaryOperatorTree(
					tok as any,
					this.consumePrimaryOrUnaryExpression(),
					true
				);
			case Separator.LParen: {
				// Casts
				const match = this.matchBracket(
					tok as Token & {type: Separator.LParen}
				);
				if (
					match.tokenIndex === this.currentIndex + 2 &&
					isPrimitive(this.ahead(1).type)
				) {
					// MUST be a primitive cast, e.g. (int) a
					const next = this.ahead(3); // e.g. "a" in the above
					if (SyntaxFacts.DefinitelyBinaryOperator.has(next.type)) {
						// Following expression is invalid, e.g. starts with "*" ... probably need an expression instead
						// of "int" or whatever primitive
						this.emitError(
							"Expected expression instead of primitive type",
							this.ahead(1).span
						);

						const errStart = this.currentIndex;
						const errEnd = this.currentIndex + 3;

						for (let i = 0; i < 4; ++i) this.next(); // skip '(', 'int', ')', '*'

						const rhs = this.consumeExpression();
						const lhs = new ErrorTree(this.tokens.slice(errStart, errEnd));

						return this._makeBinOp(
							next as TokenOfType<BinaryOperator>,
							lhs,
							rhs
						);
					}

					return this.consumeCast();
				}

				const following = this.ahead(match.tokenIndex + 1 - this.currentIndex);
				if (SyntaxFacts.UnaryExpressionNotPlusMinusStart.has(following.type)) {
					return this.consumeCast();
				}
			} // fallthrough
			default:
				let expr = this.consumePrimaryExpression(reason);
				if (expr === null) return null;

				// Read postfix operators
				let next = this.peek();
				while (
					next.type === Operator.PlusPlus ||
					next.type === Operator.MinusMinus
				) {
					this.next();
					expr = new UnaryOperatorTree(next as any, expr, false /* postfix */);
					next = this.peek();
				}

				return expr;
		}
	}

	captureErrors<T>(callback: () => T): {errs: CompileError[]; result: T} {
		const oldErrors = this.errors;
		const newErrors = (this.errors = []);
		const result = callback();
		this.errors = oldErrors;
		return {errs: newErrors, result};
	}

	consumeExpressionStatementList(): ExpressionStatementListTree | null {
		const stmts: ExpressionStatementTree[] = [];
		const commas: TokenOfType<Separator.Comma>[] = [];

		while (true) {
			const stmt = this.consumeExpressionStatement(false);
			if (stmt === null) break;
			stmts.push(stmt);

			const comma = this.maybeTT(Separator.Comma);

			if (comma) {
				commas.push(comma);
			} else {
				break;
			}
		}

		if (stmts.length === 0) return null;

		return new ExpressionStatementListTree(stmts, commas);
	}

	/**
	 * ForOrEnhancedForStatement:
	 *   ForStatement
	 *   EnhancedForStatement
	 *
	 * EnhancedForStatement:
	 *   for '(' {VariableModifier} Type VariableDeclaratorId : Expression ')' Statement
	 *
	 * ForStatement:
	 *   for '(' {ForInit} ';' [Expression] ';' {ForUpdate} ')' Statement
	 *
	 * ForInit:
	 *   StatementExpressionList
	 *   LocalVariableDeclaration
	 */
	consumeForOrEnhancedForStatement(): BaseTree | null {
		const forToken = this.expectTT(Keyword.for);
		if (forToken === null) return null;
		const openParen = this.expectTT(Separator.LParen, false);

		const annots = this.consumeOptionalAnnotationList();

		// Init: First try to consume a type and see what follows
		const start = this.currentIndex;
		const finalToken = this.maybeTT(Keyword.final);
		const {result: ty, errs} = this.captureErrors(() => {
			return this.consumePrimitiveOrReferenceType();
		});

		ty?.attachAnnotations(annots);

		let init:
			| LocalVariableDeclarationListTree
			| ErrorTree
			| ExpressionStatementListTree
			| null = null;
		if (ty !== null) {
			// for (int pox : b)
			//          ^^^
			// OR
			// for (int a = 0; a <
			//          ^
			// OR
			// for (pox = 2
			//          ^
			const tokenFollowingTy = this.peek();
			switch (tokenFollowingTy.type) {
				case Misc.identifier: {
					if (this.ahead(1).type === Operator.Colon) {
						errs.forEach((err) => this.errors.push(err));

						// Enhanced for loop (aka range for loop)
						const ident = this.consumePlainIdentifier(" enhanced for loop")!;
						const colon = this.expectTT(Operator.Colon)!;

						const iterated = this.consumeExpression(
							" right-hand side of for loop"
						);
						const closeParen = this.expectTT(Separator.RParen, false, true);

						const block = this.consumeBlockStatement();

						return new EnhancedForLoopTree(
							forToken,
							openParen,
							finalToken,
							ty,
							ident,
							colon,
							iterated,
							closeParen,
							block
						);
					}

					// Local variable declaration list
					init = this.consumeLocalVariableDeclarationRest(ty, false);
					break;
				}
				case Separator.Semicolon:
				case Separator.Comma:
				case Separator.RParen: {
					// for (int;
					//         ^
					// OR
					// for (a :
					//        ^
					// (i.e. Missing type or variable name)
					ty.resolveSpans();
					if (!this._guessIsMissingTypeForVariable(ty)) {
						this.emitError("Expected variable name", this.peek().span);
					}
					init = new ErrorTree([ty]);
					break;
				}
				default: {
					// Actually a statement expression
					break;
				}
			}
		}

		if (init === null) {
			this.rollback(start);
			if (this.peek().type !== Separator.Semicolon) {
				init = this.consumeExpressionStatementList();
			}
		} else {
			errs.forEach((err) => this.errors.push(err));
		}

		const sep1 = this.expectTT(Separator.Semicolon);
		const forCond =
			this.peek().type === Separator.Semicolon
				? null
				: this.consumeExpression();
		const sep2 = this.expectSemicolon();
		const update =
			this.peek().type === Separator.RParen
				? null
				: this.consumeExpressionStatementList();
		const closeParen = this.expectTT(Separator.RParen, false, true);

		return new ForLoopTree(
			forToken,
			openParen,
			init,
			sep1,
			forCond,
			sep2,
			update,
			closeParen,
			this.consumeBlockStatement()
		);
	}

	/**
	 * Consume a while statement:
	 *
	 * WhileStatement:
	 *   'while' '(' Expression ')' BlockStatement
	 */
	consumeWhileStatement(): WhileLoopTree | null {
		const whileToken = this.expectTT(Keyword.while);
		if (!whileToken) return null;

		const {openParen, condition, closeParen} =
			this._consumeParenthesizedCondition("while-condition");
		const statement = this.consumeBlockStatement();

		return new WhileLoopTree(
			whileToken,
			openParen,
			condition,
			closeParen,
			statement
		);
	}

	/**
	 * Consume a do-while statement:
	 *
	 * 'do' BlockStatement 'while' '(' Expression ')' ';'
	 */
	consumeDoWhileStatement(): DoWhileLoopTree | null {
		const doToken = this.expectTT(Keyword.do);
		if (doToken === null) return null;

		const stmt = this.consumeBlockStatement();
		const whileToken = this.expectTT(Keyword.while);

		const {openParen, condition, closeParen} =
			this._consumeParenthesizedCondition("do-while condition");
		const semicolon = this.expectSemicolon();

		return new DoWhileLoopTree(
			doToken,
			stmt,
			whileToken,
			openParen,
			condition,
			closeParen,
			semicolon
		);
	}

	/**
	 * catch (FormalParameter) Block
	 *
	 * FormalParameter:
	 *   {VariableModifier} Type VariableDeclaratorId
	 */
	_consumeOptionalCatches(): CatchStatementTree[] {
		const catchToken = this.maybeTT(Keyword.catch);
		if (!catchToken) return [];

		const openParen = this.expectTT(Separator.LParen);
		const referenceTys: ReferenceTypeTree[] = [];

		const finalToken = this.maybeTT(Keyword.final);

		while (!this.eof()) {
			const ty = this.consumeReferenceType(false, false) as ReferenceTypeTree;
			if (ty === null) {
				this.emitError("Expected catch type", this.peek().span);
				break;
			}
			referenceTys.push(ty);
			if (this.peek().type !== Operator.Or) {
				break;
			}
			this.next(); // consume the Or
		}

		const ident = this.consumePlainIdentifier(" as part of a catch statement");
		const closeParen = this.expectTT(Separator.RParen, false, true);
		const block = this.consumeBlock();

		const result: CatchStatementTree[] = [
			new CatchStatementTree(
				catchToken,
				openParen,
				finalToken,
				referenceTys,
				ident,
				closeParen,
				block
			),
		];
		if (this.peek().type === Keyword.catch) {
			result.push(...this._consumeOptionalCatches());
		}
		return result;
	}

	/**
	 * Finally:
	 *   'finally' Block
	 */
	_consumeOptionalFinally() {
		const finallyToken = this.maybeTT(Keyword.finally);
		if (!finallyToken) return null;

		const block = this.consumeBlock();
		return new FinallyStatementTree(finallyToken, block);
	}

	_consumeTryWithResourcesSpecification():
		| (LocalVariableDeclarationListTree | IdentTree)[]
		| null {
		const lparen = this.expectTT(Separator.LParen);
		cafebabeAssert(
			lparen !== null,
			"try with resources specification precondition"
		);

		const decls: (LocalVariableDeclarationListTree | IdentTree)[] = [];

		do {
			// No modifiers allowed, except final
			if (this.peek().type === Separator.RParen) {
				break;
			}

			if (this.javaVersion() >= 9 && this.peek().type === Misc.identifier) {
				const ahead = this.ahead(1);
				if (
					ahead.type === Separator.RParen ||
					ahead.type === Separator.Semicolon
				) {
					// Try-with-resources statement with just a variable name, e.g. (try (a) { ... })
					decls.push(this.consumePlainIdentifier(" in variable name")!);
					continue;
				}
			}

			const finalToken = this.maybeTT(Keyword.final);
			const ty = this.consumePrimitiveOrReferenceType(
				false,
				false,
				false,
				true
			);
			if (ty === null) {
				this.emitError(
					"Expected type in try with resources specification",
					this.peek().span
				);
				break;
			}

			const decl = this.consumePlainIdentifier(" in variable name");
			if (decl === null) break;

			const equals = this.expectTT(Operator.Eq);
			const expr = this.consumeExpression();

			decls.push(
				new LocalVariableDeclarationListTree(
					ty,
					[new LocalVariableDeclarationTree(decl, null, equals, expr)],
					[],
					null,
					finalToken ? [new ModifierTree(finalToken, "final")] : []
				)
			);
		} while (this.maybeTT(Separator.Semicolon));

		// TODO ATTACH
		this.expectTT(Separator.RParen, false, true);

		return decls;
	}

	/**
	 * TryStatement:
	 *   try Block [Catches] [Finally]
	 */
	consumeTryStatement(): TryStatementTree | null {
		const tryToken = this.expectTT(Keyword.try);
		if (tryToken === null) return null;

		const next = this.peek();
		const resources =
			next.type === Separator.LParen
				? this._consumeTryWithResourcesSpecification()
				: null;

		// try without arguments, e.g. try { throw new RuntimeException(); } catch (e) {}
		const block = this.consumeBlock();
		const catches = this._consumeOptionalCatches();
		const finallyBlock = this._consumeOptionalFinally();

		if (catches.length === 0 && finallyBlock === null && resources === null) {
			this.emitError("Try block missing catch/finally", tryToken.span);
		}

		return new TryStatementTree(
			tryToken,
			resources,
			block,
			catches,
			finallyBlock
		);
	}

	/**
	 * ReturnStatement:
	 *   'return' [Expression] ;
	 */
	consumeReturnStatement(): ReturnStatementTree | null {
		const returnToken = this.expectTT(Keyword.return);
		if (returnToken === null) return null;

		let expr: BaseExpressionTree | null = null;
		if (this.peek().type !== Separator.Semicolon)
			expr = this.consumeExpression();
		const semi = this.expectSemicolon();

		return new ReturnStatementTree(returnToken, expr, semi);
	}

	/**
	 * BreakStatement
	 *   'break' [label];
	 */
	consumeBreakStatement(): BreakStatementTree | null {
		const breakToken = this.expectTT(Keyword.break);
		if (breakToken === null) return null;

		let lab: IdentTree | null = null;
		if (this.peek().type !== Separator.Semicolon)
			lab = this.consumePlainIdentifier(" as part of a labelled break");
		const semi = this.expectSemicolon();

		return new BreakStatementTree(breakToken, lab, semi);
	}

	/**
	 * ContinueStatement
	 *   'continue' [label];
	 */
	consumeContinueStatement(): ContinueStatementTree | null {
		const continueToken = this.expectTT(Keyword.continue);
		if (continueToken === null) return null;

		let lab: IdentTree | null = null;
		if (this.peek().type !== Separator.Semicolon) {
			lab = this.consumePlainIdentifier(" as part of a labelled break");
		}
		const semi = this.expectSemicolon();
		return new ContinueStatementTree(continueToken, lab, semi);
	}

	/**
	 * ThrowStatement
	 *   'throw' Expression ';'
	 */
	consumeThrowStatement(): BaseTree | null {
		const throwToken = this.expectTT(Keyword.throw);
		if (throwToken === null) return null;

		const expr = this.consumeExpression();
		const semi = this.expectSemicolon();

		return new ThrowStatementTree(throwToken, expr, semi);
	}

	consumeAssertStatement(): BaseTree | null {
		const assertToken = this.expectTT(Keyword.assert);
		if (assertToken === null) return null;

		const expr = this.consumeExpression();

		const colon = this.maybeTT(Operator.Colon);
		let message: BaseExpressionTree | null = null;

		if (colon) {
			message = this.consumeExpression();
		}

		const semi = this.expectSemicolon();

		return new AssertTree(assertToken, expr, colon, message, semi);
	}

	/**
	 * SynchronizedStatement
	 *   'synchronized' '(' Expression ')' Block
	 */
	consumeSynchronizedStatement(): BaseTree | null {
		const synchronizedToken = this.expectTT(Keyword.synchronized);
		if (synchronizedToken === null) return null;

		const {
			openParen,
			condition: expr,
			closeParen,
		} = this._consumeParenthesizedCondition("synchronization value");

		const block = this.consumeBlock();

		return new SynchronizedStatementTree(
			synchronizedToken,
			openParen,
			expr,
			closeParen,
			block
		);
	}

	/**
	 * Consume a switch statement.
	 */
	consumeSwitchStatement(): SwitchStatementTree | null {
		const switchToken = this.expectTT(Keyword.switch);
		if (switchToken === null) return null;

		const {
			openParen,
			condition: expr,
			closeParen,
		} = this._consumeParenthesizedCondition("switch condition");

		const lbrace = this.expectTT(Separator.LBrace, false);
		// Repeatedly consume labels and block statements
		const components: CaseStatementTree[] = [];

		main: while (!this.eof()) {
			const next = this.peek();
			switch (next.type) {
				case Keyword.case: {
					// e.g. case 1 + 2:
					const caseToken = this.expectTT(Keyword.case)!;
					const caseExpr = this.consumeExpression(" for case label");
					const colon = this.expectTT(Operator.Colon, false);

					components.push(
						new CaseStatementTree(caseToken, caseExpr, colon, [])
					);
					break;
				}
				case Keyword.default: {
					// e.g. default:
					const defaultToken = this.expectTT(Keyword.default)!;
					const colon = this.expectTT(Operator.Colon, false);

					components.push(new CaseStatementTree(defaultToken, null, colon, []));
					break;
				}
				case Separator.RBrace: {
					break main;
				}
				default: {
					// Consume block statement
					const idx = this.currentIndex;
					const stmt = this.consumeBlockStatement();
					if (!stmt) {
						if (this.currentIndex === idx) this.next(); // force progress
						continue;
					}

					const last = components.at(-1);
					if (!last) {
						this.emitError("Expected case or default statement", stmt.span);
					} else {
						last.statements.push(stmt);
					}
				}
			}
		}

		const rbrace = this.expectTT(Separator.RBrace);
		return new SwitchStatementTree(
			switchToken,
			openParen,
			expr,
			closeParen,
			lbrace,
			components,
			rbrace
		);
	}

	/**
	 * EmptyStatement:
	 *   ';'
	 */
	consumeEmptyStatement(): EmptyStatementTree | null {
		const tok = this.expectTT(Separator.Semicolon);
		if (tok === null) return null;
		return new EmptyStatementTree(tok);
	}

	consumeArrayInitializer(): ArrayInitializerTree | null {
		const open = this.expectTT(Separator.LBrace);
		if (open === null) return null;

		const exprs: BaseExpressionTree[] = [];
		const commas: TokenOfType<Separator.Comma>[] = [];
		while (!this.eof()) {
			if (this.peek().type === Separator.RBrace) {
				break;
			}
			const expr = this.consumeExpressionOrArrayInitializerList();
			if (expr === null) break;
			exprs.push(expr);
			if (this.peek().type === Separator.Comma) {
				commas.push(this.expectTT(Separator.Comma)!);
			} else {
				break;
			}
		}
		return new ArrayInitializerTree(
			open,
			exprs,
			commas,
			this.expectTT(Separator.RBrace)
		);
	}

	/**
	 * Right-hand side of a variable declaration. Note that array initializers cannot appear as sub-expressions --
	 * hence this top-level rule.
	 */
	consumeExpressionOrArrayInitializerList(): BaseExpressionTree | null {
		return this.peek().type === Separator.LBrace
			? this.consumeArrayInitializer()
			: this.consumeExpression();
	}

	/**
	 * Parse the trailing part of a local variable declaration, that is, the parts after the type.
	 *
	 * Examples:
	 *   a = 0, b[] = null;
	 */
	consumeLocalVariableDeclarationRest(
		tt: PrimitiveTypeTree | ReferenceTypeTree | ArrayTypeTree,
		includeSemi: boolean
	): LocalVariableDeclarationListTree | null {
		const decls: LocalVariableDeclarationTree[] = [];
		const commas: TokenOfType<Separator.Comma>[] = [];
		const ty = new LocalVariableDeclarationListTree(
			tt,
			decls,
			commas,
			null,
			[]
		);

		main: while (!this.eof()) {
			const tok = this.peek();
			switch (tok.type) {
				case Misc.identifier: {
					const ident = new IdentTree(tok);
					let bracketsOpt: BracketsTree | null = null;
					let expr: BaseExpressionTree | null = null;
					let eqToken: TokenOfType<Operator.Eq> | null = null;

					this.next();
					let next = this.peek();
					if (next.type === Separator.LBracket) {
						bracketsOpt = this.consumeOptionalBrackets();
						next = this.peek();
					}
					if (next.type === Operator.Eq) {
						this.next();
						eqToken = next as TokenOfType<Operator.Eq>;
						expr = this.consumeExpressionOrArrayInitializerList();
						next = this.peek();
					}

					switch (next.type) {
						case Separator.Comma: {
							// TODO emit tree.commas
							commas.push(next as TokenOfType<Separator.Comma>);
							this.next();
							decls.push(
								new LocalVariableDeclarationTree(
									ident,
									bracketsOpt,
									eqToken,
									expr
								)
							);
							break;
						}
						case Separator.Semicolon: {
							// done
							if (includeSemi) {
								ty.semi = next as TokenOfType<Separator.Semicolon>;
								this.next();
							}
							decls.push(
								new LocalVariableDeclarationTree(
									ident,
									bracketsOpt,
									eqToken,
									expr
								)
							);
							break main;
						}
						default:
							decls.push(
								new LocalVariableDeclarationTree(
									ident,
									bracketsOpt,
									eqToken,
									expr
								)
							);
							this.emitError(
								"Expected comma or semicolon",
								Parser.spanFollowing(this.ahead(-1))
							);
							break main;
					}

					break;
				}
				default: {
					this.emitError("Expected variable name", tok.span);
					break main;
				}
			}
		}

		if (decls.length === 0)
			this.emitError("Expected variable name", this.peek().span);
		ty.decls = decls;
		return ty;
	}

	/**
	 * BlockStatement:
	 *   LocalVariableDeclarationStatement
	 *   ClassDeclaration
	 *   Statement
	 */
	consumeBlockStatement(): BaseTree | null {
		const comment = this.getComment();

		const annotations = this.consumeOptionalAnnotationList();
		const modifiers: ModifierTree[] = [];

		main: while (!this.eof()) {
			const tok = this.peek();
			switch (tok.type) {
				case Keyword.public:
				case Keyword.protected:
				case Keyword.private:
				case Keyword.abstract:
				case Keyword.static:
				case Keyword.strictfp: // must be a class or enum declaration
				case Keyword.class:
				case Keyword.enum:
				case Keyword.interface: // disallowed
					return (
						this.consumeClassOrEnumDeclaration()
							?.attachAnnotations(annotations)
							?.attachModifiers(modifiers)
							?.attachDocComment(comment) ?? null
					);
				case Keyword.final:
				case Keyword.transient:
					modifiers.push(new ModifierTree(tok, tokenTypeToString(tok.type)));
					this.next();
					continue;
				case Misc.identifier:
				case Keyword.int:
				case Keyword.long:
				case Keyword.short:
				case Keyword.byte:
				case Keyword.char:
				case Keyword.float:
				case Keyword.double:
				case Keyword.boolean: {
					// Attempt to parse a type and see if the next token is an identifier, followed by an equals or
					// comma or opening bracket. If so, parse as a local variable declaration.
					const save = this.currentIndex;
					let ty = null as
						| ReferenceTypeTree
						| ArrayTypeTree
						| PrimitiveTypeTree
						| null;
					const {errs} = this.captureErrors(() => {
						ty = this.consumePrimitiveOrReferenceType(
							false,
							false,
							false,
							true
						);
					});

					if (ty) {
						const tokFollowingTy = this.peek();
						switch (tokFollowingTy.type) {
							case Misc.identifier:
								this.errors.push(...errs);
								return (
									this.consumeLocalVariableDeclarationRest(
										ty,
										true
									)?.setModifiers(modifiers) ?? null
								);
							case Separator.Dot: {
								// Could be a .class or .super
								break;
							}
							case Separator.Semicolon:
								// Missing variable name, e.g. ArrayList<Integer>;
								this.emitError("Expected variable name", tokFollowingTy.span);
								const semi = this.maybeTT(Separator.Semicolon);

								this.errors.push(...errs);
								return (
									new LocalVariableDeclarationListTree(
										ty,
										[],
										[],
										semi,
										[]
									).setModifiers(modifiers) ?? null
								);
						}
					}
					if (ty instanceof ReferenceTypeTree && ty.typeArgs) {
						this.errors.push(...errs);
						let span = this.ahead(-1)!.span;
						span = {start: span.end, end: span.end + 1};
						this.emitError("Expected variable name", span);
						return new ErrorTree([ty]);
					}

					this.rollback(save);
				} // fallthrough
				default: {
					return this.consumeStatement();
				}
			}
		}

		return null;
	}

	/**
	 * Block:
	 *   '{' {BlockStatement} '}'
	 *
	 * Additionally, synchronize to the end of the block -- as determined by brackets matching -- if the full thing
	 * is not consumed.
	 */
	consumeBlockWithSynchronization(): BlockTree | null {
		const open = this.peek();
		if (open.type !== Separator.LBrace) return null;

		const matching = this.matchBracket(open as TokenOfType<Separator.LBrace>);
		const block = this.consumeBlock();

		this.currentIndex = Math.max(this.currentIndex, matching.tokenIndex + 1);
		return block;
	}

	/**
	 * Block:
	 *   '{' {BlockStatement} '}'
	 */
	consumeBlock(): BlockTree | null {
		const open = this.expectTT(Separator.LBrace);
		if (open === null) return null;

		const stmts: BaseTree[] = [];

		main: while (!this.eof()) {
			switch (this.peek().type) {
				case Separator.RBrace:
					break main;
				default:
					const stmt = this.consumeBlockStatement();
					if (stmt) {
						stmts.push(stmt);
					} else {
						this.synchronize(SyntaxFacts.StatementStartOrTerminal);
						if (this.peek().type === Separator.Semicolon) {
							this.next();
						}
					}
					break;
			}
		}

		const close = this.expectTT(Separator.RBrace);
		return new BlockTree(stmts, open, close);
	}

	/**
	 * StaticInitialisationBlock:
	 *
	 * 'static' Block
	 */
	consumeStaticInitializationBlock(): StaticInitializationBlockTree | null {
		const staticToken = this.expectTT(Keyword.static);
		if (staticToken === null) return null;

		const block = this.consumeBlock();
		if (block === null) return null;

		return new StaticInitializationBlockTree(block, staticToken);
	}

	static spanFollowing(tok: Token): Span {
		return {start: tok.span.end, end: tok.span.end + 1};
	}

	/**
	 * For example for a function declaration with no implementation.
	 */
	consumeBlockOrEmpty(): BlockTree | null {
		const semi = this.maybeTT(Separator.Semicolon);
		if (semi) return null;

		const block = this.consumeBlockWithSynchronization();
		if (!block)
			this.emitError(
				"Expected body of function",
				Parser.spanFollowing(this.ahead(-1))
			);
		return block;
	}

	/**
	 * ConstructorRest:
	 *   ClassName '(' [FunctionArguments] ')' [Throws] (Block | ;)
	 */
	consumeConstructorRest(): FunctionDeclarationTree | null {
		const className = this.expectTT(Misc.identifier);
		if (className === null) return null;

		const functionArguments = this.consumeFunctionArgumentsAndThrows();
		const block = this.consumeBlockOrEmpty();

		return new FunctionDeclarationTree(
			[],
			block,
			null,
			null,
			className,
			functionArguments,
			true /* is ctor */
		);
	}

	/**
	 * 'throws' {ReferenceType}
	 */
	_consumeOptionalThrows(): ThrowsTree | null {
		const throwsToken = this.maybeTT(Keyword.throws);
		if (!throwsToken) return null;

		const types: ReferenceTypeTree[] = [];
		while (!this.eof()) {
			const ty = this.consumeReferenceType(
				false /* disallow arrays */
			) as ReferenceTypeTree | null;
			if (ty === null) {
				this.emitError("Expected exception type", this.peek().span);
				this.next();
			} else {
				types.push(ty);
			}

			if (this.peek().type !== Separator.Comma) break;
			this.next();
		}

		return new ThrowsTree(throwsToken, types);
	}

	/**
	 * Function arguments:
	 *   '(' [FunctionArguments] ')' [Throws]
	 */
	consumeFunctionArgumentsAndThrows(
		permitThrows = true
	): FunctionArgumentsTree | null {
		const openParen = this.expectTT(Separator.LParen);
		if (openParen === null) return null;

		const args: FunctionArgumentTree[] = [];
		const commas: Token[] = [];

		main: while (!this.eof()) {
			let tok = this.peek();
			let finalToken: Token | null = null;
			switch (tok.type) {
				case Separator.RParen:
					break main;
				case Keyword.final:
					finalToken = tok;
					this.next();
					tok = this.peek(); // fallthrough
				default:
					const type = this.consumePrimitiveOrReferenceType(
						true,
						false /* disallow void */,
						false,
						true
					);
					if (this._guessIsMissingTypeForVariable(type)) break;

					const ellipsis = this.maybeTT(Separator.Ellipsis);

					const arg = new FunctionArgumentTree(
						type,
						finalToken,
						this.consumePlainIdentifier(" for variable name"),
						this.consumeOptionalBrackets(),
						ellipsis
					);
					args.push(arg);

					if (ellipsis && arg.brackets) {
						this.emitError(
							"C-style array declarations cannot be used with varargs",
							ellipsis.span
						);
					}

					const next = this.peek();
					if (next.type === Separator.RParen) {
						break main;
					} else {
						const comma = this.maybeTT(Separator.Comma)!;
						if (comma) {
							commas.push(comma);
							if (this.peek().type === Separator.RParen)
								this.emitError("Expected function argument", this.peek().span);
						} else {
							this.emitError("Expected ',' or ')'", this.peek().span);
							break main;
						}
					}
			}
		}

		const closeParen = this.expectTT(Separator.RParen, false, true);
		return new FunctionArgumentsTree(
			openParen,
			args,
			commas,
			closeParen,
			this.consumeOptionalBrackets(),
			this._consumeOptionalThrows()
		);
	}

	getComment(): Comment | null {
		return this.lexer.commentMap.get(this.peek()) ?? null;
	}

	/**
	 * ClassBodyDeclaration:
	 *   ClassMemberDeclaration
	 *   InstanceInitializer
	 *   StaticInitializer
	 *   ConstructorDeclaration
	 */
	consumeClassBodyDeclaration(className = ""): BaseTree | null {
		const start = this.currentIndex;

		const modifiers: ModifierTree[] = []; // not known until later what we're modifying
		const annotations: AnnotationTree[] = [];
		let tyParams: TypeParametersTree | null = null;

		const comment = this.getComment();

		let result: BaseTree | null = null;
		main: while (!this.eof()) {
			const tok = this.peek();
			switch (tok.type) {
				case Keyword.interface:
				case Keyword.class:
				case Keyword.enum:
					result = this.consumeTypeDeclaration();
					break main;
				case Separator.LBrace: {
					// Initializer block
					result = this.consumeBlockWithSynchronization();
					break main;
				}
				case Separator.At:
					if (this.ahead(1).type === Keyword.interface) {
						result = this._consumeAnnotationTypeDeclaration();
						break main;
					} else {
						const annot = this.consumeAnnotation();
						if (!annot) {
							// this.synchronise()
						} else {
							annotations.push(annot);
						}
					}
					continue;
				case Keyword.static:
					if (this.ahead(1).type === Separator.LBrace) {
						// Static initialisation block, e.g. static { a = System.getCurrentTimeMillis(); }
						for (const mod of modifiers)
							this.emitError("Modifier not allowed here", mod.span);

						return this.consumeStaticInitializationBlock();
					} // fallthrough
				case Keyword.public:
				case Keyword.protected:
				case Keyword.private:
				case Keyword.abstract:
				case Keyword.final:
				case Keyword.strictfp:
				case Keyword.native:
				case Keyword.synchronized:
				case Keyword.transient:
				case Keyword.volatile:
					modifiers.push(new ModifierTree(tok, tokenTypeToString(tok.type)));
					break;
				case Keyword.int:
				case Keyword.short:
				case Keyword.byte:
				case Keyword.long:
				case Keyword.double:
				case Keyword.char:
				case Keyword.float:
				case Keyword.boolean:
				case Keyword.void: // void function
				case Misc.identifier: // function return type or constructor name
					if (this.ahead(1).type === Separator.LParen) {
						// constructor
						const ctorRest =
							this.consumeConstructorRest()?.setModifiers(modifiers);
						if (ctorRest) ctorRest.typeParams = tyParams;
						return ctorRest ?? null;
					} else {
						// Attempt to parse a type
						const ty = this.consumePrimitiveOrReferenceType(
							true,
							true /* allow void */,
							false,
							true /* allow wildcard */
						)!;
						const fnOrMemberName = this.expectTT(Misc.identifier);
						if (fnOrMemberName === null) {
							if (ty) {
								return new ErrorTree([...modifiers, ty]);
							} else {
								return new ErrorTree(
									this.tokens.slice(start, this.currentIndex)
								);
							}
						}

						const next = this.peek();
						switch (next.type) {
							case Separator.LParen: {
								// Function definition with name fnOrMemberName returning ty (possibly with some extra
								// arrays if C-style -- TODO)

								const args = this.consumeFunctionArgumentsAndThrows();
								const block = this.consumeBlockOrEmpty();

								result = new FunctionDeclarationTree(
									modifiers,
									block,
									tyParams,
									ty,
									fnOrMemberName,
									args,
									false /* not ctor */
								);
								break main;
							}
							case Separator.LBracket: // C-style array decl
							case Operator.Eq:
							case Separator.Comma:
							// Member variable declaration with initial value
							// Step back to declaration name and parse rest
							case Separator.Semicolon: {
								// Member variable declaration
								this.rollback(this.currentIndex - 1);
								result =
									this.consumeLocalVariableDeclarationRest(
										ty,
										true
									)?.setModifiers(modifiers) ?? null;
							}
						}
					}
					break main;
				case Operator.Lt:
					if (tyParams)
						this.emitError("Type arguments already specified", tok.span);
					tyParams = this._consumeTypeParameters();
					continue;
				case Separator.Semicolon:
					// Allowed for some reason...
					this.next();
					return new EmptyStatementTree(
						tok as TokenOfType<Separator.Semicolon>
					);
			}

			this.next();
		}

		if (!result) {
			this.emitError("Expected class member declaration", this.peek().span);
			result = new ErrorTree(this.tokens.slice(start, this.currentIndex));
		}

		result.attachDocComment(comment);
		result.attachAnnotations(annotations);
		if (result.isTypeDeclaration()) {
			result.attachModifiers(modifiers);
		}
		return result;
	}

	/**
	 * ClassOrEnumDeclaration:
	 *   ClassDeclaration
	 *   EnumDeclaration
	 */
	consumeClassOrEnumDeclaration():
		| ClassDeclarationTree
		| EnumDeclarationTree
		| null {
		const modifiers: ModifierTree[] = [];
		const annotations: AnnotationTree[] = [];

		let result: ClassDeclarationTree | EnumDeclarationTree | null = null;
		main: while (!this.eof()) {
			const tok = this.peek();
			switch (tok.type) {
				case Separator.At:
					const annot = this.consumeAnnotation();
					if (annot === null) {
						this.synchronize(SyntaxFacts.TypeDeclarationModifierOrStart);
					} else {
						annotations.push(annot);
					}
					break;
				case Keyword.public:
				case Keyword.protected:
				case Keyword.private:
				case Keyword.abstract:
				case Keyword.static:
				case Keyword.final:
				case Keyword.strictfp:
					// Consume the modifier
					this.next();
					modifiers.push(new ModifierTree(tok, tokenTypeToString(tok.type)));
					break;
				case Keyword.interface:
					this.emitError("Interface not allowed here", tok.span);
					this.next();
					return null;
				case Keyword.class:
					result =
						this._consumeClassOrInterfaceDeclaration() as ClassDeclarationTree;
					break main;
				case Keyword.enum:
					result = this._consumeEnumDeclaration();
					break main;
				default:
					this.emitError("Expected class or enum declaration", tok.span);
					this.synchronize(SyntaxFacts.TypeDeclarationModifierOrStart);
					break main;
			}
		}

		result?.attachAnnotations(annotations);
		result?.attachModifiers(modifiers);

		return result;
	}

	/**
	 * TypeParameter:
	 *   {Annotation} Identifier [TypeBound]
	 * TypeBound:
	 *   extends TypeVariable
	 *   extends ClassOrInterfaceType {AdditionalBound}
	 */
	consumeTypeParameter(): TypeParameterTree | null {
		const annotations = this.consumeOptionalAnnotationList();
		const ident = this.expectTT(Misc.identifier);
		if (ident === null) return null;

		let extends_: ReferenceTypeTree | null = null;
		let bounds: BoundsTree | null = null;
		const extendsToken = this.maybeTT(Keyword.extends);
		if (extendsToken) {
			extends_ = this.consumeReferenceType(false) as ReferenceTypeTree | null;
			bounds = this._consumeOptionalBounds();
		}

		return new TypeParameterTree(
			new IdentTree(ident),
			extendsToken,
			extends_,
			bounds
		).attachAnnotations(annotations);
	}

	consumeOptionalImplementsOrExtendsClause(
		type: Keyword.implements | Keyword.extends
	): ReferenceTypeTree[] | null {
		if (this.peek().type === type) {
			// Parse superinterfaces
			this.next();
			const superinterfaces: ReferenceTypeTree[] = [];
			while (true) {
				const iface = this.consumeReferenceType(
					false
				) as ReferenceTypeTree | null;
				if (iface === null) {
					// TODO
					//this.synchronise(SyntaxFacts.InterfaceTypeOrEnd);
				} else {
					superinterfaces.push(iface);
				}

				if (this.peek().type === Separator.Comma) {
					this.next();
				} else {
					return superinterfaces;
				}
			}
		} else {
			return null;
		}
	}

	/**
	 * ClassDeclaration:
	 *   class Identifier [TypeParameters] [Superclass] [Superinterfaces] ClassBody
	 *
	 * TypeParameters:
	 *   < TypeParameter {, TypeParameter} >
	 *
	 * Superclass:
	 *   extends ClassType
	 *
	 * Superinterfaces:
	 *   implements InterfaceTypeList
	 */
	_consumeClassOrInterfaceDeclaration():
		| ClassDeclarationTree
		| InterfaceDeclarationTree
		| null {
		const next = this.peek();
		const isClass = next.type === Keyword.class;
		if (!isClass && next.type !== Keyword.interface) {
			this.emitError("Expected class or interface declaration", next.span);
			return null;
		}

		this.next();

		const className = this.consumePlainIdentifier("class name");
		if (className === null) return null;

		const typeArgs = this._consumeTypeParameters();

		let superclass: ReferenceTypeTree[] | null = null;
		if (this.peek().type === Keyword.extends) {
			// Parse superclass
			superclass = this.consumeOptionalImplementsOrExtendsClause(
				Keyword.extends
			);
		}

		if (isClass && (superclass?.length ?? 0) > 1) {
			this.emitError("Cannot extend multiple classes", superclass![1]!.span);
		}

		const superinterfaces: ReferenceTypeTree[] | null =
			this.consumeOptionalImplementsOrExtendsClause(Keyword.implements);
		if (superinterfaces && !isClass) {
			this.emitError(
				"Extend superinterfaces with extends, not implements",
				this.peek().span
			);
		}

		const openBrace = this.expectTT(Separator.LBrace);
		// Parse class body
		const body = this.consumeClassOrInterfaceBody(className.identifier);

		const closeBrace = this.expectTT(Separator.RBrace);

		if (isClass) {
			return new ClassDeclarationTree(
				[],
				next as TokenOfType<Keyword.class>,
				className,
				typeArgs,
				superclass?.[0] ?? null,
				superinterfaces,
				openBrace,
				body,
				closeBrace
			);
		} else {
			return new InterfaceDeclarationTree(
				[],
				next as TokenOfType<Keyword.interface>,
				className,
				openBrace,
				body,
				closeBrace,
				typeArgs,
				superclass
			);
		}
	}

	consumeEnumConstant() {
		const ident = this.expectTT(Misc.identifier);
		if (!ident) return null;

		const openParen = this.maybeTT(Separator.LParen);
		let args: BaseExpressionTree[] | null = null;
		if (openParen) {
			args = this.consumeMethodInvocationArguments(null).exprs;
			this.expectTT(Separator.RParen, false, true);
		}

		let body: BaseTree[] | null = null;
		if (this.peek().type === Separator.LBrace) {
			this.expectTT(Separator.LBrace)!;
			body = this.consumeClassOrInterfaceBody(ident.value);
			this.expectTT(Separator.RBrace);
		}

		return new EnumConstantTree(ident, args, body);
	}

	/**
	 * Consume the body of an enum declaration.
	 */
	consumeEnumBody(): [
		EnumConstantTree[],
		BaseTree[],
		TokenOfType<Separator.LBrace> | null,
		TokenOfType<Separator.RBrace> | null,
	] {
		const lbrace = this.expectTT(Separator.LBrace);

		const constants: EnumConstantTree[] = [];
		let hasDeclarationsFollowing = false;
		constants: while (!this.eof()) {
			const tok = this.peek();
			switch (tok.type) {
				case Misc.identifier: {
					const enumConstant = this.consumeEnumConstant();
					const next = this.peek();

					if (enumConstant) constants.push(enumConstant);

					if (next.type === Separator.Comma) {
						this.next();
						continue;
					} else if (
						next.type === Separator.Semicolon ||
						next.type === Separator.RBrace
					) {
						hasDeclarationsFollowing = next.type === Separator.Semicolon;
						if (hasDeclarationsFollowing) this.next();
						break constants;
					} else {
						this.emitError("Expected comma or semicolon", next.span);
						break constants;
					}
				}
				case Separator.Semicolon: {
					this.next();
					hasDeclarationsFollowing = true;
					break constants;
				}
				default:
					break constants;
			}
		}

		// Read class body declaratinos
		const body: BaseTree[] = [];
		if (hasDeclarationsFollowing) {
			main: while (!this.eof()) {
				const tok = this.peek();
				switch (tok.type) {
					case Separator.RBrace:
						break main;
					default:
						const decl = this.consumeClassBodyDeclaration();
						if (decl) body.push(decl);
						break; // TODO Sync
				}
			}
		}

		const rbrace = this.expectTT(Separator.RBrace);
		return [constants, body, lbrace, rbrace];
	}

	_consumeEnumDeclaration() {
		const enumToken = this.expectTT(Keyword.enum);
		if (enumToken === null) return null;

		const enumName = this.consumePlainIdentifier(" as part of enum name");
		const superinterfaces = this.consumeOptionalImplementsOrExtendsClause(
			Keyword.implements
		);
		const [constants, body, openBrace, closeBrace] = this.consumeEnumBody();
		return new EnumDeclarationTree(
			[],
			openBrace,
			body,
			closeBrace,
			enumToken,
			enumName,
			superinterfaces,
			constants
		);
	}

	/**
	 * TypeDeclaration:
	 *   {ClassModifier} class Identifier ClassDeclarationRest
	 *   {ClassModifier} enum Identifier EnumDeclarationRest
	 *   {InterfaceModifier} interface Identifier InterfaceDeclarationRest
	 *   {InterfaceModifier} @ interface Indetifier AnnotationDeclarationRest
	 *
	 * ClassModifier:
	 *   (one of) Annotation public protected private abstract static final strictfp
	 * InterfaceModifier:
	 *   (one of) Annotation public protected private abstract static strictfp
	 */
	consumeTypeDeclaration(): BaseTree | null {
		const modifiers: ModifierTree[] = [];
		const annotations: AnnotationTree[] = [];

		if (this.peek().type === Separator.Semicolon) {
			this.next();
			return new EmptyStatementTree(
				this.peek() as TokenOfType<Separator.Semicolon>
			);
		}

		const comment = this.getComment();

		modifiers: while (true) {
			const tok = this.peek();
			switch (tok.type) {
				case Separator.At: // look one ahead to see if it's an @interface declaration
					if (this.ahead(1).type === Keyword.interface) break modifiers;

					const annot = this.consumeAnnotation();
					if (annot === null) {
						// any modifier, @, interface, enum, class
						this.synchronize(SyntaxFacts.TypeDeclarationModifierOrStart);
					} else {
						annotations.push(annot);
					}
					break;
				case Keyword.public:
				case Keyword.protected:
				case Keyword.private:
				case Keyword.abstract:
				case Keyword.static:
				case Keyword.final:
				case Keyword.strictfp:
					// Consume the modifier
					this.next();
					modifiers.push(new ModifierTree(tok, tokenTypeToString(tok.type)));
					break;
				case Keyword.class:
				case Keyword.interface:
				case Keyword.enum:
					break modifiers;
				default:
					this.emitError("Expected class declaration", tok.span);
					return null;
			}
		}

		let result: BaseTree | null = null;
		switch (this.peek().type) {
			case Misc.eof:
				return null;
			case Separator.At:
				result = this._consumeAnnotationTypeDeclaration();
				break;
			case Keyword.class:
			case Keyword.interface:
				result = this._consumeClassOrInterfaceDeclaration();
				break;
			case Keyword.enum:
				result = this._consumeEnumDeclaration();
				break;
			default:
				cafebabeAssert(false, "unreachable consumeTypeDeclaration");
				return null;
		}

		result?.attachAnnotations(annotations)?.attachDocComment(comment);
		if (result?.isTypeDeclaration()) result.attachModifiers(modifiers);
		return result;
	}

	/**
	 * PackageDeclarationNoAnnotations:
	 *   package Identifier {. Identifier} ;
	 */
	consumePackageDeclaration(): PackageDeclarationTree | null {
		const packageToken = this.expectTT(Keyword.package);
		if (packageToken === null) return null;

		const qual = this.consumeQualifiedIdentifier(QualIdentMode.Normal);
		const semiToken = this.expectSemicolon();
		return new PackageDeclarationTree(qual, packageToken, semiToken);
	}

	/**
	 * ImportDeclaration:
	 *  import [static] Identifier {. Identifier} ;
	 */
	consumeImportDeclaration(): ImportDeclarationTree | null {
		const importToken = this.expectTT(Keyword.import);
		if (importToken === null) return null;

		const maybeStaticToken = this.maybeTT(Keyword.static);
		const qual = this.consumeQualifiedIdentifier(
			QualIdentMode.AllowTrailingStar
		);
		if (qual === null) return null;

		let starToken: (Token & {type: Operator.Star}) | null = null;
		if (
			qual instanceof NameTree &&
			qual.identifier.identifier === "" &&
			this.peek().type === Operator.Star
		) {
			//                  v star
			// import java.util.*;
			//        ^^^^^^^^^^
			//           qual
			starToken = this.expectTT(Operator.Star);
		}

		const semi = this.expectSemicolon();
		return new ImportDeclarationTree(
			qual,
			importToken,
			maybeStaticToken,
			semi,
			starToken
		);
	}

	/**
	 * Create a compilation unit (top-level goal symbol).
	 *
	 * CompilationUnit:
	 *   [PackageDeclaration] {ImportDeclaration} {TypeDeclaration}
	 */
	consumeCompilationUnit(): CompilationUnitTree {
		let packageDecl: PackageDeclarationTree | null = null;
		const annotations = this.consumeOptionalAnnotationList();

		if (this.peek().type === Keyword.package) {
			packageDecl =
				this.consumePackageDeclaration()?.attachAnnotations(annotations) ??
				null;
			if (packageDecl === null)
				// failed to parse package
				this.synchronize(SyntaxFacts.StartImportOrTypeDeclOrPackage, true);
		}

		let classesAlreadyDefined = false;
		const imports: ImportDeclarationTree[] = [];
		const typeDecls: BaseTree[] = [];

		while (!this.eof()) {
			const tok = this.peek();
			switch (tok.type) {
				case Separator.At:
				default:
					annotations.push(...this.consumeOptionalAnnotationList());
					const typeDeclaration = this.consumeTypeDeclaration();
					if (typeDeclaration !== null) {
						classesAlreadyDefined = true;
						typeDeclaration.attachAnnotations(annotations);
						typeDecls.push(typeDeclaration);
					} else {
						this.next(); // force advance
					}
					break;
				case Keyword.package: {
					this.emitError(
						packageDecl !== null
							? "Duplicate package declaration"
							: "Package declaration not allowed here",
						tok.span
					);
					this.consumePackageDeclaration();
					break;
				}
				case Keyword.import: {
					const import_ = this.consumeImportDeclaration();
					if (!import_) break;
					if (classesAlreadyDefined)
						this.emitError("Import statement not allowed here", tok.span);
					imports.push(import_);
					break;
				}
			}
			annotations.length = 0;
			this.synchronize(SyntaxFacts.StartImportOrTypeDeclOrPackage, true); // no-op if what follows is valid
		}

		return new CompilationUnitTree(packageDecl, imports, typeDecls);
	}

	/**
	 * Scenarios to differentiate for error messages (^ is current position):
	 *   f(a,)   vs.   f(int,)    vs. f(ArrayList,)
	 *      ^               ^                    ^
	 * In the first case it's more likely that the type is missing, in the third case it's more likely that the
	 * variable name is missing. We detect the first case and emit an error, and return true; or return false if
	 * it's probably one of the other two cases.
	 * @returns If it is believed that the type is missing rather than the variable name.
	 */
	private _guessIsMissingTypeForVariable(
		tyJustParsed: ReferenceTypeTree | ArrayTypeTree | PrimitiveTypeTree | null
	) {
		const tok = this.peek().type;
		if (
			tok === Separator.Comma ||
			tok === Separator.RParen ||
			tok === Operator.Colon ||
			tok === Separator.Semicolon
		) {
			// Missing variable name, or maybe missing type. Roughly distinguish these cases using the capitalisation
			// of the "type" just parsed.
			let name: string;
			if (
				tyJustParsed instanceof ReferenceTypeTree &&
				!tyJustParsed.typeArgs &&
				!tyJustParsed.lhs &&
				vaguelyClassifyName((name = tyJustParsed.prettyPrint())) ===
					GuessedNameClass.VariableName
			) {
				// cow(p,)
				//     ^ Variable p needs a type
				this.emitError(`Variable ${name} needs a type`, this.ahead(-1).span);
				this.maybeTT(Separator.Comma); // skip comma
				return true;
			}
		}
		return false;
	}

	private _consumeTypeParameters(): TypeParametersTree | null {
		this._startTypeSection(); // want to parse >> as > >

		const lt = this.maybeTT(Operator.Lt);
		if (!lt) {
			this._endTypeSection();
			return null;
		}

		const typeArgs: TypeParameterTree[] = [];
		const commas: TokenOfType<Separator.Comma>[] = [];
		let gt: TokenOfType<Operator.Gt> | null = null;
		while (true) {
			const typeArg = this.consumeTypeParameter();
			if (typeArg === null) {
				// TODO synchronisation point
				//this.synchronise(SyntaxFacts.TypeParameterOrEnd);
				break;
			} else {
				typeArgs.push(typeArg);
			}

			gt = this.maybeTT(Operator.Gt);
			if (gt) break;

			const comma = this.maybeTT(Separator.Comma);
			if (comma) {
				commas.push(comma);
			} else {
				this.emitError(
					"Expected comma or closing angle bracket",
					this.peek().span
				);
				break;
			}
		}
		this._endTypeSection();
		return new TypeParametersTree(lt, typeArgs, commas, gt);
	}

	consumeWildcard(): WildcardTree | null {
		const ques = this.expectTT(Operator.Question);
		if (!ques) return null;

		const bound = this.peek();
		if (bound.type === Keyword.extends || bound.type === Keyword.super) {
			// Wildcard like ? extends Enum
			this.next();
			return new WildcardTree(
				ques,
				bound as TokenOfType<Keyword.extends | Keyword.super>,
				this.consumeReferenceType()
			);
		}

		return new WildcardTree(ques, null, null); // unbounded wildcard
	}
}
